import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import Drawer from "@mui/material/Drawer";
import InputAdornment from "@mui/material/InputAdornment";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { ADJUSTMENT_OPTIONS } from "./locationAdjustmentOptions";
import { toast } from "react-toastify";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_labor_plan_summary } from "../../../constants";

const MODE_BOTTOM_UP = "bottom_up";
const MODE_TARGET_SPLH = "target_splh";
const MODE_TARGET_LABOR_PERCENT = "target_labor_percent";

const TARGET_DYNAMIC = "dynamic";
const TARGET_LABOR_PERCENT = "labor_percent";
const TARGET_SPLH = "splh";

const TARGET_OPTIONS = [
  {
    key: TARGET_DYNAMIC,
    title: "Dynamic",
    description: "Use the labor model baseline, with an optional percent adjustment.",
  },
  {
    key: TARGET_LABOR_PERCENT,
    title: "Fixed labor %",
    description: "Set an exact weekly labor percent target.",
  },
  {
    key: TARGET_SPLH,
    title: "Fixed SPLH",
    description: "Set an exact weekly sales-per-labor-hour target.",
  },
];
const ADJUSTMENT_PERCENT_VALUES = ADJUSTMENT_OPTIONS.map(
  (option) => option.laborTargetPercent
);
const ADJUSTMENT_PERCENT_MIN = Math.min(...ADJUSTMENT_PERCENT_VALUES);
const ADJUSTMENT_PERCENT_MAX = Math.max(...ADJUSTMENT_PERCENT_VALUES);
const ADJUSTMENT_PERCENT_LABEL_BY_VALUE = ADJUSTMENT_OPTIONS.reduce(
  (labels, option) => ({
    ...labels,
    [option.laborTargetPercent]: option.label,
  }),
  {}
);
const ADJUSTMENT_PERCENT_MARKS = Array.from(
  {
    length: Math.floor((ADJUSTMENT_PERCENT_MAX - ADJUSTMENT_PERCENT_MIN) / 5) + 1,
  },
  (_, index) => ADJUSTMENT_PERCENT_MIN + index * 5
).map((value) => ({
  value,
  label:
    value === ADJUSTMENT_PERCENT_MIN ||
    value === ADJUSTMENT_PERCENT_MAX ||
    value === 0
      ? ADJUSTMENT_PERCENT_LABEL_BY_VALUE[value] || ""
      : "",
}));

const TIMEFRAME_OPTIONS = ["4W", "13W", "YTD", "All"];
const COMPARISON_SCHEDULED_FORECASTED = "scheduled_forecasted";
const COMPARISON_MODE_CONFIGS = {
  [COMPARISON_SCHEDULED_FORECASTED]: {
    key: COMPARISON_SCHEDULED_FORECASTED,
    label: "Scheduled vs Forecasted",
    primaryKey: "scheduled",
    comparisonKey: "forecasted",
    primaryLabel: "Scheduled labor",
    comparisonLabel: "Forecasted labor",
    salesLabel: "Forecasted sales",
    ratioLabel: "of forecasted",
    chartTitle: "Scheduled vs forecasted labor",
    emptyLabel: "scheduled and forecasted labor",
  },
};
const TABLE_COMPARISON_CONFIG =
  COMPARISON_MODE_CONFIGS[COMPARISON_SCHEDULED_FORECASTED];
const LABOR_PLAN_CHART_CONFIG = {
  chartTitle: "Company labor plan trend",
  actualLabel: "Actual used labor",
  earnedLabel: "Earned labor",
  forecastedLabel: "Forecasted plan",
  scenarioLabel: "Draft forecast",
  emptyLabel: "historical labor and future forecasts",
};
const COMPANY_SERIES_METRIC_KEYS = ["actual", "earned", "forecasted", "planned"];
const LABOR_PLAN_TOOLTIP_WIDTH = 280;
const LABOR_PLAN_TOOLTIP_HEIGHT_ESTIMATE = 190;
const LABOR_PLAN_TOOLTIP_GAP = 14;
const STORE_TREND_TOOLTIP_WIDTH = 280;
const LABOR_PLAN_REFRESH_OVERLAY_STYLE = {
  left: "calc(60px + var(--ops-dock-width, 0px))",
};

function getNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
}

function formatNumber(value, digits = 1) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  return numberValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatFixedNumber(value, digits = 2) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  return numberValue.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatCurrency(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  const sign = numberValue < 0 ? "-" : "";
  return `${sign}${Math.abs(numberValue).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatPercent(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  return `${formatNumber(numberValue, 1)}%`;
}

function formatSplh(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  return `${formatNumber(numberValue, 1)} SPLH`;
}

function formatSignedPercent(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  if (Math.abs(numberValue) < 0.05) {
    return "0%";
  }
  return `${numberValue > 0 ? "+" : ""}${formatPercent(numberValue)}`;
}

function getAdjustmentClassificationLabel(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "Dynamic";
  }

  return ADJUSTMENT_OPTIONS.reduce((closestOption, option) => {
    const closestDistance = Math.abs(
      numberValue - closestOption.laborTargetPercent
    );
    const optionDistance = Math.abs(numberValue - option.laborTargetPercent);
    if (
      optionDistance < closestDistance ||
      (optionDistance === closestDistance &&
        Math.abs(option.laborTargetPercent) >
          Math.abs(closestOption.laborTargetPercent))
    ) {
      return option;
    }
    return closestOption;
  }, ADJUSTMENT_OPTIONS[0]).label;
}

function formatDynamicAdjustmentLabel(value) {
  return `${getAdjustmentClassificationLabel(value)} ${formatSignedPercent(value)}`;
}

function formatDraftVsCurrentTargetSummary(
  percentDelta,
  subject = "Draft",
  comparisonLabel = "forecasted plan"
) {
  const numberValue = getNumber(percentDelta);
  if (numberValue === null) {
    return `${subject} impact is unavailable`;
  }
  if (Math.abs(numberValue) < 0.05) {
    return `${subject} does not change ${comparisonLabel}`;
  }
  const isIncrease = numberValue > 0;
  return (
    <>
      {subject} {isIncrease ? "increases" : "decreases"} {comparisonLabel} by{" "}
      <span
        className={
          isIncrease
            ? "font-semibold text-rose-700"
            : "font-semibold text-emerald-700"
        }
      >
        {formatPercent(Math.abs(numberValue))}
      </span>
    </>
  );
}

function formatStoreNameList(names, maxVisible = 2) {
  const visibleNames = (names || []).filter(Boolean);
  if (visibleNames.length === 0) {
    return "";
  }
  if (visibleNames.length === 1) {
    return visibleNames[0];
  }
  if (visibleNames.length <= maxVisible) {
    return `${visibleNames.slice(0, -1).join(", ")} and ${
      visibleNames[visibleNames.length - 1]
    }`;
  }

  return `${visibleNames.slice(0, maxVisible).join(", ")}, and ${
    visibleNames.length - maxVisible
  } more`;
}

function getDraftStoreSummaryText(draftSummary) {
  if (!draftSummary) {
    return "Draft impact is unavailable";
  }
  if (draftSummary.validationMessage) {
    return draftSummary.validationMessage;
  }
  return formatDraftVsCurrentTargetSummary(
    draftSummary.percentDelta,
    "Draft",
    "current forecast"
  );
}

function getDraftCurrentForecastImpactDisplay(percentDelta) {
  const numberValue = getNumber(percentDelta);
  if (numberValue === null) {
    return {
      prefix: "Draft impact unavailable",
      emphasis: "",
      suffix: "",
      className: "border-slate-200 bg-white/80 text-slate-600",
    };
  }
  if (Math.abs(numberValue) < 0.05) {
    return {
      prefix: "No forecast change",
      emphasis: "",
      suffix: "",
      className: "border-slate-200 bg-white/80 text-slate-600",
    };
  }

  const isOver = numberValue > 0;
  return {
    prefix: `${isOver ? "Increases" : "Decreases"} forecast by `,
    emphasis: formatPercent(Math.abs(numberValue)),
    suffix: "",
    className: isOver
      ? "border-rose-100 bg-rose-50/80 text-slate-600"
      : "border-emerald-100 bg-emerald-50/80 text-slate-600",
    emphasisClassName: isOver
      ? "font-semibold text-rose-700"
      : "font-semibold text-emerald-700",
  };
}

function clampAdjustmentPercent(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return 0;
  }
  return Math.max(
    ADJUSTMENT_PERCENT_MIN,
    Math.min(ADJUSTMENT_PERCENT_MAX, numberValue)
  );
}

function formatDeltaHours(value) {
  const numberValue = getNumber(value);
  if (numberValue === null) {
    return "-";
  }
  if (Math.abs(numberValue) < 0.05) {
    return "0 hrs";
  }
  return `${numberValue > 0 ? "+" : ""}${formatNumber(numberValue, 0)} hrs`;
}

function getLaborPlanPercentDelta(targetMetric, comparisonMetric, laborPercentEnabled) {
  const comparisonLaborPercent = getNumber(comparisonMetric?.labor_percent);
  const targetLaborPercent = getNumber(targetMetric?.labor_percent);

  if (
    laborPercentEnabled &&
    comparisonLaborPercent !== null &&
    targetLaborPercent !== null &&
    comparisonLaborPercent > 0
  ) {
    return (
      ((targetLaborPercent - comparisonLaborPercent) / comparisonLaborPercent) *
      100
    );
  }

  const comparisonSplh = getNumber(comparisonMetric?.splh);
  const targetSplh = getNumber(targetMetric?.splh);
  if (comparisonSplh !== null && targetSplh !== null && targetSplh > 0) {
    return (comparisonSplh / targetSplh - 1) * 100;
  }

  const comparisonHours = getNumber(comparisonMetric?.hours);
  const targetHours = getNumber(targetMetric?.hours);
  if (comparisonHours !== null && targetHours !== null && comparisonHours > 0) {
    return ((targetHours - comparisonHours) / comparisonHours) * 100;
  }

  return null;
}

function getHoursPercentDelta(targetMetric, comparisonMetric) {
  const comparisonHours = getNumber(comparisonMetric?.hours);
  const targetHours = getNumber(targetMetric?.hours);
  if (comparisonHours !== null && targetHours !== null && comparisonHours > 0) {
    return ((targetHours - comparisonHours) / comparisonHours) * 100;
  }
  return null;
}

function getPercentDeltaTone(percentDelta) {
  const numberValue = getNumber(percentDelta);
  if (numberValue === null || Math.abs(numberValue) < 0.05) {
    return "muted";
  }
  return numberValue > 0 ? "success" : "danger";
}

function getMetricGroup(planRow, metricKey) {
  return planRow?.[metricKey] || {};
}

function getComparisonMetricsFromGroups(primary, comparison) {
  const primaryHours = getNumber(primary?.hours);
  const comparisonHours = getNumber(comparison?.hours);
  const primaryCost = getNumber(primary?.labor_cost);
  const comparisonCost = getNumber(comparison?.labor_cost);
  const deltaHours =
    primaryHours !== null && comparisonHours !== null
      ? primaryHours - comparisonHours
      : null;
  const costDelta =
    primaryCost !== null && comparisonCost !== null
      ? primaryCost - comparisonCost
      : null;
  const percentOfComparison =
    primaryHours !== null && comparisonHours
      ? (primaryHours / comparisonHours) * 100
      : null;

  return {
    primary,
    comparison,
    primaryHours,
    comparisonHours,
    deltaHours,
    costDelta,
    percentOfComparison,
  };
}

function getComparisonMetrics(source, config) {
  return getComparisonMetricsFromGroups(
    getMetricGroup(source, config.primaryKey),
    getMetricGroup(source, config.comparisonKey)
  );
}

function buildMetricFromHours(hours, sales, laborCost, fallbackAverageWage) {
  const normalizedHours = getNumber(hours);
  const normalizedSales = getNumber(sales);
  let normalizedLaborCost = getNumber(laborCost);

  if (
    normalizedLaborCost === null &&
    normalizedHours !== null &&
    fallbackAverageWage
  ) {
    normalizedLaborCost = normalizedHours * fallbackAverageWage;
  }

  return {
    hours: normalizedHours,
    sales: normalizedSales,
    labor_cost: normalizedLaborCost,
    labor_percent:
      normalizedSales && normalizedLaborCost
        ? (normalizedLaborCost / normalizedSales) * 100
        : null,
    splh:
      normalizedSales && normalizedHours
        ? normalizedSales / normalizedHours
        : null,
  };
}

function getPlanSales(planRow) {
  return (
    getNumber(planRow?.planned?.sales) ??
    getNumber(planRow?.forecasted?.sales) ??
    getNumber(planRow?.actual?.sales)
  );
}

function applyFixedTargetDisplayValue(metric, targetChoice, targetValue) {
  const numericTarget = getNumber(targetValue);
  if (numericTarget === null) {
    return metric;
  }
  if (targetChoice === TARGET_LABOR_PERCENT) {
    return {
      ...metric,
      labor_percent: numericTarget,
    };
  }
  if (targetChoice === TARGET_SPLH) {
    return {
      ...metric,
      splh: numericTarget,
    };
  }
  return metric;
}

function buildPlanPreviewMetric(
  planRow,
  nextHours,
  costDelta,
  fallbackAverageWage,
  referenceMetric = planRow?.planned || {}
) {
  const referenceLaborCost = getNumber(referenceMetric?.labor_cost);
  const normalizedCostDelta = getNumber(costDelta);
  const previewLaborCost =
    referenceLaborCost !== null && normalizedCostDelta !== null
      ? referenceLaborCost + normalizedCostDelta
      : referenceLaborCost;

  return buildMetricFromHours(
    nextHours,
    getPlanSales(planRow),
    previewLaborCost,
    fallbackAverageWage
  );
}

function getScenarioComparisonMetrics(row, config, scenarioHours) {
  const currentMetrics = getComparisonMetrics(row.planRow, config);
  const savedTargetMetric = getTargetMetric(row);
  const normalizedScenarioHours = getNumber(scenarioHours);
  const savedTargetHours = getNumber(savedTargetMetric?.hours);
  const averageWage = row.targetAverageWage ?? row.averageWage;
  const scenarioCostDelta =
    normalizedScenarioHours !== null &&
    savedTargetHours !== null &&
    getNumber(averageWage) !== null
      ? (normalizedScenarioHours - savedTargetHours) * getNumber(averageWage)
      : null;
  const plannedMetric = buildPlanPreviewMetric(
    row.planRow,
    scenarioHours,
    scenarioCostDelta,
    averageWage,
    savedTargetMetric
  );

  return getComparisonMetricsFromGroups(currentMetrics.primary, plannedMetric);
}

function getAdjustmentPercentFromFitFactor(fitFactor) {
  const normalizedFitFactor = getNumber(fitFactor);
  if (normalizedFitFactor === null) {
    return null;
  }
  return (normalizedFitFactor - 1) * 100;
}

function getEffectiveDynamicAdjustmentPercent(store, planRow) {
  const fitFactor = store?.labor_model_fit_factor ?? planRow?.labor_model_fit_factor;
  return getAdjustmentPercentFromFitFactor(fitFactor);
}

function getFitFactorFromAdjustmentPercent(adjustmentPercent) {
  const normalizedAdjustment = getNumber(adjustmentPercent);
  if (normalizedAdjustment === null || Math.abs(normalizedAdjustment) < 0.0001) {
    return null;
  }
  return 1 + normalizedAdjustment / 100;
}

function getLaborPlanDraftValues(targetChoice, targetValue, adjustmentValue) {
  const normalizedTarget = getNumber(targetValue);
  const normalizedAdjustment = getNumber(adjustmentValue);
  let normalizedMode = null;
  let targetToSave = null;
  let fitFactorToSave = null;

  if (targetChoice === TARGET_DYNAMIC) {
    fitFactorToSave = getFitFactorFromAdjustmentPercent(adjustmentValue);
  } else if (targetChoice === TARGET_LABOR_PERCENT) {
    normalizedMode = MODE_TARGET_LABOR_PERCENT;
    targetToSave = normalizedTarget;
  } else if (targetChoice === TARGET_SPLH) {
    normalizedMode = MODE_TARGET_SPLH;
    targetToSave = normalizedTarget;
  }

  return {
    normalizedTarget,
    normalizedAdjustment,
    normalizedMode,
    targetToSave,
    fitFactorToSave,
  };
}

function getLaborPlanDraftChange(row, draftValues) {
  const originalMode =
    row?.store?.labor_budget_mode || row?.planRow?.labor_budget_mode || MODE_BOTTOM_UP;
  const originalTarget =
    originalMode === MODE_BOTTOM_UP
      ? null
      : row?.store?.labor_budget_target_value ?? row?.planRow?.labor_budget_target_value;
  const originalFitFactor =
    row?.store?.labor_model_fit_factor ??
    row?.planRow?.labor_model_fit_factor ??
    null;
  const originalModeToCompare =
    originalMode === MODE_BOTTOM_UP ? null : originalMode;
  const targetChanged =
    draftValues.normalizedMode !== originalModeToCompare ||
    !nullableNumbersEqual(draftValues.targetToSave, originalTarget);
  const fitFactorChanged = !nullableNumbersEqual(
    draftValues.fitFactorToSave,
    originalFitFactor
  );

  return {
    targetChanged,
    fitFactorChanged,
    hasScenarioChange: targetChanged || fitFactorChanged,
  };
}

function getLaborPlanDraftValidationMessage(
  targetChoice,
  targetValue,
  adjustmentValue,
  fixedLaborPercentAvailable = true
) {
  const normalizedTarget = getNumber(targetValue);
  const normalizedAdjustment = getNumber(adjustmentValue);

  if (targetChoice === TARGET_DYNAMIC) {
    const draftFitFactor = getFitFactorFromAdjustmentPercent(normalizedAdjustment);
    if (
      normalizedAdjustment !== null &&
      (normalizedAdjustment < ADJUSTMENT_PERCENT_MIN ||
        normalizedAdjustment > ADJUSTMENT_PERCENT_MAX)
    ) {
      return `Dynamic adjustment must be between ${formatSignedPercent(
        ADJUSTMENT_PERCENT_MIN
      )} and ${formatSignedPercent(ADJUSTMENT_PERCENT_MAX)}`;
    }
    if (draftFitFactor !== null && draftFitFactor <= 0) {
      return "Dynamic adjustment must leave some labor in the plan";
    }
    return null;
  }

  if (targetChoice === TARGET_LABOR_PERCENT) {
    if (!fixedLaborPercentAvailable) {
      return "Fixed labor percent requires labor percent reporting and recent labor cost";
    }
    return !normalizedTarget || normalizedTarget <= 0
      ? "Enter a positive labor percent"
      : null;
  }

  if (targetChoice === TARGET_SPLH) {
    return !normalizedTarget || normalizedTarget <= 0
      ? "Enter a positive SPLH target"
      : null;
  }

  return "Choose a target method";
}

function getSavedDraftInput(store, planRow) {
  const initialChoice = getInitialTargetChoice(store, planRow);
  const target = store?.labor_budget_target_value ?? planRow?.labor_budget_target_value;
  const savedAdjustment = getEffectiveDynamicAdjustmentPercent(store, planRow);

  return {
    targetChoice: initialChoice,
    targetValue: target === null || target === undefined ? "" : String(target),
    adjustmentValue:
      savedAdjustment === null || savedAdjustment === undefined
        ? "0"
        : String(Number(savedAdjustment.toFixed(2))),
  };
}

function draftInputsEqual(a, b) {
  return (
    a?.targetChoice === b?.targetChoice &&
    String(a?.targetValue ?? "") === String(b?.targetValue ?? "") &&
    String(a?.adjustmentValue ?? "") === String(b?.adjustmentValue ?? "")
  );
}

function buildLaborPlanChangePayload(row, draft) {
  const draftValues = getLaborPlanDraftValues(
    draft.targetChoice,
    draft.targetValue,
    draft.adjustmentValue
  );

  return {
    store_id: row.store.id,
    labor_budget_mode: draftValues.normalizedMode,
    labor_budget_target_value: draftValues.targetToSave,
    labor_model_fit_factor: draftValues.fitFactorToSave,
  };
}

function getScenarioTargetSummary(targetChoice, targetValue, adjustmentValue) {
  const normalizedTarget = getNumber(targetValue);
  const normalizedAdjustment = getNumber(adjustmentValue);

  if (targetChoice === TARGET_LABOR_PERCENT && normalizedTarget) {
    return {
      label: `${formatPercent(normalizedTarget)} labor target`,
      detail: "Unsaved scenario",
    };
  }
  if (targetChoice === TARGET_SPLH && normalizedTarget) {
    return {
      label: `${formatCurrency(normalizedTarget)} SPLH target`,
      detail: "Unsaved scenario",
    };
  }
  if (targetChoice === TARGET_DYNAMIC) {
    return {
      label:
        normalizedAdjustment === null
          ? "Dynamic labor model"
          : formatDynamicAdjustmentLabel(normalizedAdjustment),
      detail: "Unsaved scenario",
    };
  }
  return {
    label: "Dynamic labor model",
    detail: "Unsaved scenario",
  };
}

function getLaborPlanScenario(
  row,
  config,
  targetChoice,
  targetValue,
  adjustmentValue
) {
  if (!row?.planRow) {
    return null;
  }

  const draftValues = getLaborPlanDraftValues(
    targetChoice,
    targetValue,
    adjustmentValue
  );
  const draftChange = getLaborPlanDraftChange(row, draftValues);
  if (!draftChange.hasScenarioChange) {
    return null;
  }

  const preview = getPreviewPlan(
    targetChoice,
    targetValue,
    adjustmentValue,
    row.planRow
  );
  if (getNumber(preview.hours) === null) {
    return null;
  }

  const currentMetrics = getComparisonMetrics(row.planRow, config);
  const metrics = getScenarioComparisonMetrics(row, config, preview.hours);
  const displayMetrics = {
    ...metrics,
    comparison: applyFixedTargetDisplayValue(
      metrics.comparison,
      targetChoice,
      targetValue
    ),
  };
  if (getNumber(displayMetrics.comparisonHours) === null) {
    return null;
  }

  return {
    storeId: row.store?.id,
    metrics: displayMetrics,
    preview,
    targetSummary: getScenarioTargetSummary(
      targetChoice,
      targetValue,
      adjustmentValue
    ),
    deltaHours:
      getNumber(displayMetrics.comparisonHours) -
      (getNumber(currentMetrics.comparisonHours) || 0),
  };
}

function getTargetMetric(row) {
  const plannedHours = getNumber(row?.planRow?.planned?.hours);
  const plannedSplh = getNumber(row?.planRow?.planned?.splh);
  const sales = getPlanSales(row?.planRow);
  const mode =
    row?.planRow?.labor_budget_mode || row?.store?.labor_budget_mode || MODE_BOTTOM_UP;
  const targetValue =
    row?.planRow?.labor_budget_target_value ?? row?.store?.labor_budget_target_value;
  const explicitSplh =
    mode === MODE_TARGET_SPLH ? getNumber(targetValue) : null;
  const explicitLaborPercent =
    mode === MODE_TARGET_LABOR_PERCENT ? getNumber(targetValue) : null;
  const dynamicAdjustment = getEffectiveDynamicAdjustmentPercent(
    row?.store,
    row?.planRow
  );
  const visibleLabel =
    mode === MODE_TARGET_SPLH || mode === MODE_TARGET_LABOR_PERCENT
      ? "Fixed target"
      : dynamicAdjustment !== null && Math.abs(dynamicAdjustment) > 0.0001
        ? "Dynamic adjusted"
        : "Dynamic model";
  const savedDraftInput = getSavedDraftInput(row?.store, row?.planRow);
  const savedPreview = getPreviewPlan(
    savedDraftInput.targetChoice,
    savedDraftInput.targetValue,
    savedDraftInput.adjustmentValue,
    row?.planRow
  );
  const targetHours = getNumber(savedPreview.hours) ?? plannedHours;
  const targetMetric = buildMetricFromHours(
    targetHours,
    sales,
    row?.planRow?.planned?.labor_cost,
    row?.targetAverageWage ?? row?.averageWage
  );

  return {
    visibleLabel,
    detail: row?.targetSummary?.detail || "Uses the labor model baseline",
    splh: explicitSplh ?? targetMetric.splh ?? plannedSplh,
    labor_percent: explicitLaborPercent ?? targetMetric.labor_percent,
    hours: targetHours,
    sales: targetMetric.sales,
    labor_cost: targetMetric.labor_cost,
  };
}

function getStorePlanSortVolume(row) {
  return (
    getNumber(row?.planRow?.actual?.sales) ??
    getNumber(row?.planRow?.forecasted?.sales) ??
    getNumber(row?.planRow?.planned?.sales) ??
    0
  );
}

function getStorePlanGapScore(row) {
  const useLaborPercent = Boolean(row?.store?.labor_percent_enabled);
  const actualMetric = row?.planRow?.actual || {};
  const targetMetric = getTargetMetric(row);
  const actualValue = getNumber(
    useLaborPercent ? actualMetric.labor_percent : actualMetric.splh
  );
  const targetValue = getNumber(
    useLaborPercent ? targetMetric?.labor_percent : targetMetric?.splh
  );

  if (actualValue !== null && targetValue !== null) {
    return Math.abs(actualValue - targetValue);
  }

  const actualHours = getNumber(actualMetric.hours);
  const targetHours = getNumber(targetMetric?.hours);
  if (actualHours !== null && targetHours !== null) {
    return Math.abs(actualHours - targetHours);
  }

  return null;
}

function getStorePlanLaborImpactDisplay(
  row,
  targetMetric,
  laborPercentEnabled,
  targetLabel
) {
  const actualMetric = row?.planRow?.actual || {};
  const actualHours = getNumber(actualMetric.hours);
  const targetHours = getNumber(targetMetric?.hours);
  const actualLaborPercent = getNumber(actualMetric.labor_percent);
  const targetLaborPercent = getNumber(targetMetric?.labor_percent);
  const actualSplh = getNumber(actualMetric.splh);
  const targetSplh = getNumber(targetMetric?.splh);

  let laborDeltaPercent = null;
  if (
    laborPercentEnabled &&
    actualLaborPercent !== null &&
    targetLaborPercent !== null &&
    actualLaborPercent > 0
  ) {
    laborDeltaPercent =
      ((targetLaborPercent - actualLaborPercent) / actualLaborPercent) * 100;
  } else if (actualSplh !== null && targetSplh !== null && targetSplh > 0) {
    laborDeltaPercent = (actualSplh / targetSplh - 1) * 100;
  } else if (actualHours !== null && targetHours !== null && actualHours > 0) {
    laborDeltaPercent = ((targetHours - actualHours) / actualHours) * 100;
  }

  if (laborDeltaPercent === null) {
    return null;
  }

  const actualUnderTarget = laborDeltaPercent > 0;
  const isFlat = Math.abs(laborDeltaPercent) < 0.05;

  if (isFlat) {
    return {
      prefix: "Actual matches ",
      emphasis: targetLabel,
      suffix: " hours",
      className: "border-slate-200 bg-white text-slate-600",
      emphasisClassName: "text-slate-700",
    };
  }

  return {
    prefix: "Actual is ",
    emphasis: formatPercent(Math.abs(laborDeltaPercent)),
    suffix: ` hours ${actualUnderTarget ? "under" : "over"} ${targetLabel}`,
    className: actualUnderTarget
      ? "border-emerald-100 bg-emerald-50/80 text-slate-600"
      : "border-rose-100 bg-rose-50/80 text-slate-600",
    emphasisClassName: actualUnderTarget ? "text-emerald-700" : "text-rose-700",
  };
}

function getImpactStatementEmphasisClassName(statement) {
  if (statement?.emphasisClassName) {
    return statement.emphasisClassName;
  }
  return getCaptionToneClass(statement?.tone);
}

function ImpactStatementText({ statement }) {
  if (!statement) {
    return null;
  }

  if (typeof statement === "string") {
    return statement;
  }
  const prefix =
    typeof statement.prefix === "string"
      ? statement.prefix.trimEnd()
      : statement.prefix;
  const suffix =
    typeof statement.suffix === "string"
      ? statement.suffix.trimStart()
      : statement.suffix;
  const emphasisNeedsGap = prefix && statement.emphasis;
  const suffixNeedsGap =
    statement.emphasis &&
    suffix &&
    !(typeof suffix === "string" && /^[.,;:!?]/.test(suffix));

  return (
    <>
      {prefix}
      {statement.emphasis ? (
        <span
          className={`${getImpactStatementEmphasisClassName(statement)} ${
            emphasisNeedsGap ? "ml-1" : ""
          }`}
        >
          {statement.emphasis}
        </span>
      ) : null}
      {suffix ? <span className={suffixNeedsGap ? "ml-1" : ""}>{suffix}</span> : null}
    </>
  );
}

function MetricImpactStatementBlock({ statement, statements }) {
  const visibleStatements = (statements || [statement]).filter(Boolean);

  if (visibleStatements.length === 0) {
    return null;
  }

  return (
    <div className="mt-5 space-y-2 border-t border-slate-200 pt-4">
      {visibleStatements.map((visibleStatement, index) => (
        <p
          key={`${visibleStatement.prefix || visibleStatement}-${index}`}
          className="text-sm font-medium leading-5 text-slate-600"
        >
          <ImpactStatementText statement={visibleStatement} />
        </p>
      ))}
    </div>
  );
}

function getActualVsEarnedImpactDisplay(planRow) {
  const actualHours = getNumber(planRow?.actual?.hours);
  const earnedHours = getNumber(planRow?.earned?.hours);

  if (actualHours === null || earnedHours === null || earnedHours <= 0) {
    return null;
  }

  const deltaPercent = ((actualHours - earnedHours) / earnedHours) * 100;
  const isMoreLabor = deltaPercent > 0;
  const isFlat = Math.abs(deltaPercent) < 0.05;

  if (isFlat) {
    return {
      prefix: "Actual matches ",
      emphasis: "earned",
      suffix: " hours",
      className: "border-slate-200 bg-white text-slate-600",
      emphasisClassName: "text-slate-700",
    };
  }

  return {
    prefix: "Actual is ",
    emphasis: formatPercent(Math.abs(deltaPercent)),
    suffix: ` hours ${isMoreLabor ? "over" : "under"} earned`,
    className: isMoreLabor
      ? "border-rose-100 bg-rose-50/80 text-slate-600"
      : "border-emerald-100 bg-emerald-50/80 text-slate-600",
    emphasisClassName: isMoreLabor ? "text-rose-700" : "text-emerald-700",
  };
}

function compareStorePlansByGap(a, b) {
  const aGap = getStorePlanGapScore(a);
  const bGap = getStorePlanGapScore(b);

  if (aGap !== null && bGap !== null && Math.abs(aGap - bGap) > 0.0001) {
    return bGap - aGap;
  }
  if (aGap !== null && bGap === null) {
    return -1;
  }
  if (aGap === null && bGap !== null) {
    return 1;
  }

  const volumeDelta = getStorePlanSortVolume(b) - getStorePlanSortVolume(a);
  if (Math.abs(volumeDelta) > 0.0001) {
    return volumeDelta;
  }

  return String(a.store?.store_name || "").localeCompare(
    String(b.store?.store_name || "")
  );
}

function normalizeNullableNumber(value) {
  const numberValue = getNumber(value);
  return numberValue === null ? null : numberValue;
}

function nullableNumbersEqual(a, b) {
  const normalizedA = normalizeNullableNumber(a);
  const normalizedB = normalizeNullableNumber(b);
  if (normalizedA === null && normalizedB === null) {
    return true;
  }
  if (normalizedA === null || normalizedB === null) {
    return false;
  }
  return Math.abs(normalizedA - normalizedB) < 0.0001;
}

function getAverageWage(planRow) {
  const cost = getNumber(planRow?.actual?.labor_cost);
  const hours = getNumber(planRow?.actual?.hours);
  if (!cost || !hours) {
    return null;
  }
  return cost / hours;
}

function getTargetAverageWage(planRow) {
  return getNumber(planRow?.recent_average_wage) ?? getAverageWage(planRow);
}

function canUseFixedLaborPercentTarget(store, planRow) {
  return (
    Boolean(store?.labor_percent_enabled) &&
    getNumber(planRow?.actual?.labor_cost) > 0 &&
    getNumber(planRow?.actual?.hours) > 0 &&
    getTargetAverageWage(planRow) > 0
  );
}

function getInitialTargetChoice(store, planRow) {
  const mode = store?.labor_budget_mode || planRow?.labor_budget_mode;
  if (mode === MODE_TARGET_LABOR_PERCENT) {
    return TARGET_LABOR_PERCENT;
  }
  if (mode === MODE_TARGET_SPLH) {
    return TARGET_SPLH;
  }
  return TARGET_DYNAMIC;
}

function getLaborBudgetMode(store, planRow) {
  return planRow?.labor_budget_mode || store?.labor_budget_mode || MODE_BOTTOM_UP;
}

function isFixedLaborBudgetMode(mode) {
  return mode === MODE_TARGET_SPLH || mode === MODE_TARGET_LABOR_PERCENT;
}

function needsDynamicBaseline(row, targetChoice) {
  if (targetChoice !== TARGET_DYNAMIC) {
    return false;
  }
  const mode = getLaborBudgetMode(row?.store, row?.planRow);
  return (
    isFixedLaborBudgetMode(mode) &&
    getNumber(row?.planRow?.bottom_up_forecasted?.hours) === null
  );
}

function getDynamicBaselineValidationMessage(
  row,
  targetChoice,
  loading,
  error
) {
  if (!needsDynamicBaseline(row, targetChoice)) {
    return null;
  }
  if (loading) {
    return "Dynamic baseline is loading.";
  }
  if (error) {
    return "Dynamic baseline could not be loaded. Try reopening this store.";
  }
  return "Dynamic baseline is loading.";
}

function getTargetSummary(store, planRow) {
  const mode = planRow?.labor_budget_mode || store?.labor_budget_mode || MODE_BOTTOM_UP;
  const targetValue = planRow?.labor_budget_target_value ?? store?.labor_budget_target_value;
  const adjustmentPercent = getEffectiveDynamicAdjustmentPercent(store, planRow);

  if (mode === MODE_TARGET_LABOR_PERCENT && targetValue) {
    return {
      label: `${formatPercent(targetValue)} labor target`,
      detail: "Fixed weekly labor percent",
    };
  }
  if (mode === MODE_TARGET_SPLH && targetValue) {
    return {
      label: `$${formatNumber(targetValue, 1)} SPLH target`,
      detail: "Fixed weekly sales per hour",
    };
  }
  if (adjustmentPercent !== null && Math.abs(adjustmentPercent) > 0.0001) {
    return {
      label: formatDynamicAdjustmentLabel(adjustmentPercent),
      detail: "Dynamic labor model adjustment",
    };
  }
  return {
    label: "Dynamic labor model",
    detail: "Uses the labor model baseline",
  };
}

function getDynamicAdjustmentDisplay(row, draft) {
  const targetChoice =
    draft?.targetChoice || getInitialTargetChoice(row?.store, row?.planRow);
  if (targetChoice !== TARGET_DYNAMIC) {
    return null;
  }

  const adjustmentPercent = draft
    ? getNumber(draft.adjustmentValue)
    : getEffectiveDynamicAdjustmentPercent(row?.store, row?.planRow);

  if (adjustmentPercent === null || Math.abs(adjustmentPercent) < 0.05) {
    return null;
  }

  const classificationLabel = getAdjustmentClassificationLabel(adjustmentPercent);

  return {
    prefix: `${classificationLabel} `,
    emphasis: formatSignedPercent(adjustmentPercent),
    suffix: "",
    className:
      adjustmentPercent > 0
        ? "border-rose-100 bg-rose-50/80 text-slate-700"
        : "border-emerald-100 bg-emerald-50/80 text-slate-700",
    emphasisClassName:
      adjustmentPercent > 0 ? "text-rose-700" : "text-emerald-700",
    adjustmentPercent,
    classificationLabel,
  };
}

function DynamicAdjustmentBadge({ display, tooltipAlign = "left" }) {
  if (!display) {
    return null;
  }

  return (
    <span className="group relative mt-2 inline-block max-w-full">
      <span
        className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold leading-5 ${display.className}`}
      >
        {display.prefix}
        <span className={display.emphasisClassName}>
          {display.emphasis}
        </span>
        {display.suffix}
        <InfoOutlinedIcon className="shrink-0 text-slate-400" sx={{ fontSize: 13 }} />
      </span>
      <span
        className={`invisible absolute top-[calc(100%+8px)] z-40 block w-[280px] rounded-lg border border-slate-200 bg-white p-4 text-left text-xs leading-5 text-slate-600 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 ${
          tooltipAlign === "right" ? "right-0" : "left-0"
        }`}
      >
        <span className="block text-sm font-semibold text-slate-900">
          Dynamic adjustment
        </span>
        <span className="mt-1 block">
          The labor model first builds a bottom-up forecast from forecasted sales and standards. This store is classified as {display.classificationLabel} because it applies a {display.emphasis} adjustment to those hours before the plan is published.
        </span>
      </span>
    </span>
  );
}

function getTargetMethodDisplay(row, draft, draftValidationMessage) {
  if (draftValidationMessage) {
    return {
      label: "Draft incomplete",
      className: "border-orange-100 bg-orange-50 text-orange-700",
      detailTitle: "Draft incomplete",
      detailDescription:
        "This store has an incomplete draft. Finish the target value before publishing labor plan changes.",
    };
  }

  const targetChoice =
    draft?.targetChoice || getInitialTargetChoice(row?.store, row?.planRow);
  const targetValue = getNumber(
    draft
      ? draft.targetValue
      : row?.store?.labor_budget_target_value ??
          row?.planRow?.labor_budget_target_value
  );

  if (targetChoice === TARGET_LABOR_PERCENT) {
    return {
      label:
        targetValue === null
          ? "Fixed"
          : `Fixed at ${formatPercent(targetValue)}`,
      className: "border-violet-100 bg-violet-50 text-violet-700",
      detailTitle: "Fixed labor percent",
      detailDescription:
        "This store uses a fixed weekly labor percent. The forecasted plan converts that target into labor hours using sales and recent average wage.",
      detailRows: [
        {
          label: "Labor Percent",
          value: formatPercent(targetValue),
        },
      ],
    };
  }

  if (targetChoice === TARGET_SPLH) {
    return {
      label:
        targetValue === null
          ? "Fixed"
          : `Fixed at ${formatSplh(targetValue)}`,
      className: "border-violet-100 bg-violet-50 text-violet-700",
      detailTitle: "Fixed SPLH",
      detailDescription:
        "This store uses a fixed sales-per-labor-hour target. The forecasted plan converts sales into labor hours using this SPLH value.",
      detailRows: [
        {
          label: "SPLH",
          value: formatSplh(targetValue),
        },
      ],
    };
  }

  return null;
}

function TargetMethodBadge({ display }) {
  if (!display) {
    return null;
  }

  return (
    <span className="group relative mt-2 inline-block max-w-full">
      <span
        className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-semibold ${display.className}`}
      >
        {display.label}
        <InfoOutlinedIcon className="shrink-0 text-slate-400" sx={{ fontSize: 13 }} />
      </span>
      <span className="invisible absolute left-0 top-[calc(100%+8px)] z-40 block w-[290px] rounded-lg border border-slate-200 bg-white p-4 text-left text-xs leading-5 text-slate-600 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <span className="block text-sm font-semibold text-slate-900">
          {display.detailTitle}
        </span>
        <span className="mt-1 block">{display.detailDescription}</span>
        {display.detailRows?.length ? (
          <span className="mt-3 block border-t border-slate-200 pt-3">
            {display.detailRows.map((row) => (
              <span
                key={row.label}
                className="flex items-center justify-between gap-4"
              >
                <span className="text-slate-500">{row.label}</span>
                <span className="font-semibold text-slate-900">{row.value}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </span>
  );
}

function getStorePlan(store, planRow) {
  const actualHours = getNumber(planRow?.actual?.hours);
  const plannedHours = getNumber(planRow?.planned?.hours);
  const rawBaselineHours = getNumber(planRow?.baseline?.hours);
  const averageWage = getAverageWage(planRow);
  const targetAverageWage = getTargetAverageWage(planRow);
  const deltaHours =
    actualHours !== null && plannedHours !== null ? plannedHours - actualHours : null;
  const costDelta =
    deltaHours !== null && averageWage !== null ? deltaHours * averageWage : null;
  const targetSummary = getTargetSummary(store, planRow);

  return {
    store,
    planRow,
    actualHours,
    plannedHours,
    rawBaselineHours,
    averageWage,
    targetAverageWage,
    deltaHours,
    costDelta,
    targetSummary,
  };
}

function getPreviewPlan(targetChoice, targetValue, adjustmentValue, planRow) {
  const currentPlanHours =
    getNumber(planRow?.planned?.hours) ?? getNumber(planRow?.forecasted?.hours);
  const bottomUpForecastedHours = getNumber(
    planRow?.bottom_up_forecasted?.hours
  );
  const forecastedHours =
    bottomUpForecastedHours ?? getNumber(planRow?.forecasted?.hours);
  const currentFitFactor = getNumber(planRow?.effective_fit_factor) || 1;
  const baselineForecastHours =
    bottomUpForecastedHours !== null
      ? bottomUpForecastedHours
      : forecastedHours !== null
        ? forecastedHours / currentFitFactor
        : currentPlanHours;
  const sales = getPlanSales(planRow);
  const averageWage = getTargetAverageWage(planRow);
  const numericTarget = getNumber(targetValue);
  const draftFitFactor = getFitFactorFromAdjustmentPercent(adjustmentValue);
  const fitFactor = draftFitFactor === null ? 1 : draftFitFactor;

  let hours = null;
  if (targetChoice === TARGET_DYNAMIC) {
    hours =
      baselineForecastHours !== null ? baselineForecastHours * fitFactor : null;
  } else if (targetChoice === TARGET_LABOR_PERCENT) {
    if (sales && averageWage && numericTarget) {
      hours = (sales * (numericTarget / 100)) / averageWage;
    }
  } else if (targetChoice === TARGET_SPLH) {
    if (sales && numericTarget) {
      hours = sales / numericTarget;
    }
  }

  const deltaHours =
    hours !== null && currentPlanHours !== null ? hours - currentPlanHours : null;
  const costDelta =
    deltaHours !== null && averageWage !== null ? deltaHours * averageWage : null;
  const percentDelta =
    deltaHours !== null && currentPlanHours > 0
      ? (deltaHours / currentPlanHours) * 100
      : null;

  return {
    hours,
    deltaHours,
    costDelta,
    percentDelta,
  };
}

function formatShortDate(dateString) {
  if (!dateString || typeof dateString !== "string") {
    return "-";
  }
  const [, month, day] = dateString.split("-");
  return month && day ? `${Number(month)}/${Number(day)}` : dateString;
}

function formatDateRange(startDate, endDate) {
  if (!startDate && !endDate) {
    return "";
  }
  if (!endDate || startDate === endDate) {
    return formatShortDate(startDate);
  }
  return `${formatShortDate(startDate)}-${formatShortDate(endDate)}`;
}

function getDateRangeDayCount(startDate, endDate) {
  if (!startDate || !endDate) {
    return null;
  }
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return null;
  }

  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
}

function getLaborPlanWindowLabels(seriesWindow = {}) {
  return {
    historical: formatDateRange(
      seriesWindow.start_date,
      seriesWindow.historical_end_date
    ),
    forecast: formatDateRange(
      seriesWindow.forecast_start_date,
      seriesWindow.forecast_end_date
    ),
  };
}

function getLaborPlanWindowDayCounts(seriesWindow = {}) {
  return {
    historical: getDateRangeDayCount(
      seriesWindow.start_date,
      seriesWindow.historical_end_date
    ),
    forecast: getDateRangeDayCount(
      seriesWindow.forecast_start_date,
      seriesWindow.forecast_end_date
    ),
  };
}

function formatStoreCount(count) {
  const numberValue = getNumber(count);
  if (numberValue === null) {
    return "-";
  }
  return `${formatNumber(numberValue, 0)} ${numberValue === 1 ? "store" : "stores"}`;
}

function getAverageDailySalesValue(sales, dayCount) {
  const numberValue = getNumber(sales);
  const normalizedDayCount = getNumber(dayCount);
  if (numberValue === null) {
    return null;
  }
  if (!normalizedDayCount || normalizedDayCount <= 0) {
    return numberValue;
  }
  return numberValue / normalizedDayCount;
}

function getAverageDailyValue(value, dayCount) {
  const numberValue = getNumber(value);
  const normalizedDayCount = getNumber(dayCount);
  if (numberValue === null) {
    return null;
  }
  if (!normalizedDayCount || normalizedDayCount <= 0) {
    return numberValue;
  }
  return numberValue / normalizedDayCount;
}

function formatAverageDailyHours(value, dayCount) {
  return formatFixedNumber(getAverageDailyValue(value, dayCount));
}

function formatAverageDailyCurrency(value, dayCount) {
  return formatCurrency(getAverageDailyValue(value, dayCount));
}

function formatDraftImpactChipLabel(percentDelta) {
  const numberValue = getNumber(percentDelta);
  if (numberValue === null) {
    return null;
  }

  return formatDraftVsCurrentTargetSummary(
    numberValue,
    "Draft",
    "current forecast"
  );
}

function getAverageDailySalesLabel(label, dayCount) {
  if (!dayCount) {
    return "Sales";
  }

  const normalizedLabel = String(label || "").toLowerCase();
  if (normalizedLabel.includes("forecast") || normalizedLabel.includes("draft")) {
    return "Daily Forecasted Sales";
  }
  if (normalizedLabel.includes("actual") || normalizedLabel.includes("earned")) {
    return "Daily Actual Sales";
  }
  return "Daily Sales";
}

function formatLabelWithWindow(label, windowLabel) {
  if (!windowLabel) {
    return label;
  }
  return `${label} (${windowLabel})`;
}

function getChartPoints(rows, valueGetter, width, height, padding, maxValue) {
  if (rows.length < 2) {
    return [];
  }

  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;

  return rows.map((row, index) => {
    const value = valueGetter(row);
    const safeValue = value === null ? 0 : value;
    const x = padding.left + (index / (rows.length - 1)) * usableWidth;
    const y =
      padding.top +
      (1 - safeValue / (maxValue || 1)) * usableHeight;

    return { row, rawValue: value, value: safeValue, x, y };
  });
}

function getChartPathFromPoints(points) {
  const availablePoints = points.filter(
    (point) => point.rawValue !== null && point.rawValue !== undefined
  );
  if (availablePoints.length < 2) {
    return "";
  }

  let segmentStarted = false;
  return points
    .map((point) => {
      if (point.rawValue === null || point.rawValue === undefined) {
        segmentStarted = false;
        return "";
      }
      const command = segmentStarted ? "L" : "M";
      segmentStarted = true;
      return `${command} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
    })
    .filter(Boolean)
    .join(" ");
}

function getScenarioSeriesRows(seriesRows, config, scenario) {
  const scenarios = Array.isArray(scenario)
    ? scenario.filter(Boolean)
    : scenario
      ? [scenario]
      : [];

  if (scenarios.length === 0 || !Array.isArray(seriesRows) || seriesRows.length === 0) {
    return null;
  }

  const totalComparisonHours = seriesRows.reduce((total, row) => {
    if (row?.period !== "forecast") {
      return total;
    }
    return total + (getScenarioSeriesBaselineHours(row, config) || 0);
  }, 0);
  const scenarioDeltaHours = scenarios.reduce(
    (total, scenarioItem) =>
      total +
      (getNumber(scenarioItem.preview?.deltaHours) ??
        getNumber(scenarioItem.deltaHours) ??
        0),
    0
  );
  const forecastRows = seriesRows.filter((row) => row?.period === "forecast");
  const fallbackShare = forecastRows.length > 0 ? 1 / forecastRows.length : 0;

  return seriesRows.map((row) => {
    if (row?.period !== "forecast") {
      return {
        ...row,
        scenario: {
          hours: null,
        },
      };
    }

    const comparisonHours = getScenarioSeriesBaselineHours(row, config) || 0;
    const share =
      totalComparisonHours > 0
        ? comparisonHours / totalComparisonHours
        : fallbackShare;
    const scenarioHours = Math.max(
      0,
      comparisonHours + scenarioDeltaHours * share
    );

    return {
      ...row,
      scenario: {
        hours: scenarioHours,
      },
    };
  });
}

function getScenarioSeriesBaselineHours(row, config) {
  if (config?.comparisonKey === "forecasted") {
    return getNumber(row?.planned?.hours) ?? getNumber(row?.forecasted?.hours);
  }
  return getNumber(row?.[config?.comparisonKey]?.hours);
}

function getSeriesRowKey(row) {
  return `${row?.date || ""}|${row?.period || ""}`;
}

function buildCompanySeriesRowsFromStores(seriesRows, storeSeriesByStoreId, storeIds) {
  const accumulators = new Map();

  seriesRows.forEach((row, index) => {
    const key = getSeriesRowKey(row);
    accumulators.set(key, {
      index,
      date: row?.date,
      period: row?.period,
      totals: COMPANY_SERIES_METRIC_KEYS.reduce((nextTotals, metricKey) => {
        nextTotals[metricKey] = {
          hours: 0,
          sales: 0,
        };
        return nextTotals;
      }, {}),
      counts: COMPANY_SERIES_METRIC_KEYS.reduce((nextCounts, metricKey) => {
        nextCounts[metricKey] = {
          hours: 0,
          sales: 0,
        };
        return nextCounts;
      }, {}),
    });
  });

  storeIds.forEach((storeId) => {
    const storeRows = storeSeriesByStoreId[String(storeId)] || [];
    storeRows.forEach((storeRow) => {
      const key = getSeriesRowKey(storeRow);
      const accumulator = accumulators.get(key);
      if (!accumulator) {
        return;
      }

      COMPANY_SERIES_METRIC_KEYS.forEach((metricKey) => {
        ["hours", "sales"].forEach((fieldKey) => {
          const value = getNumber(storeRow?.[metricKey]?.[fieldKey]);
          if (value !== null) {
            accumulator.totals[metricKey][fieldKey] += value;
            accumulator.counts[metricKey][fieldKey] += 1;
          }
        });
      });
    });
  });

  return Array.from(accumulators.values())
    .sort((a, b) => a.index - b.index)
    .map((accumulator) => {
      return {
        date: accumulator.date,
        period: accumulator.period,
        ...COMPANY_SERIES_METRIC_KEYS.reduce((nextRow, metricKey) => {
          nextRow[metricKey] = {
            hours:
              accumulator.counts[metricKey].hours > 0
                ? accumulator.totals[metricKey].hours
                : null,
            sales:
              accumulator.counts[metricKey].sales > 0
                ? accumulator.totals[metricKey].sales
                : null,
          };
          return nextRow;
        }, {}),
      };
    });
}

function MetricLoadingState({
  label = "Calculating forecast",
  detail = "Dynamic baseline",
}) {
  return (
    <div
      className="mt-2 flex min-h-[52px] items-center gap-2.5 rounded-md border border-violet-100 bg-white/80 px-2.5 py-2"
      aria-live="polite"
    >
      <CircularProgress
        size={18}
        thickness={4.5}
        sx={{ color: "#6d28d9", flexShrink: 0 }}
      />
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold leading-5 text-slate-800">
          {label}
        </p>
        <p className="truncate text-[11px] font-medium leading-4 text-slate-500">
          {detail}
        </p>
      </div>
    </div>
  );
}

function DrawerMetricPreviewCard({
  title,
  metric,
  laborPercentEnabled,
  metricWindowLabel,
  impactPercent,
  tone = "slate",
  loading = false,
}) {
  const tooltipTitle = formatLabelWithWindow(title, metricWindowLabel);
  const primaryMetric = getSummaryPrimaryMetric(metric, laborPercentEnabled);
  const secondaryMetric = getSummarySecondaryMetric(metric, laborPercentEnabled);
  const highlight = tone === "orange";
  const cardClassName = highlight
    ? "border-orange-200 bg-orange-50/70"
    : "border-slate-200 bg-slate-50";
  const labelClassName = highlight ? "text-orange-700" : "text-slate-500";
  const secondaryClassName = highlight ? "text-orange-700" : "text-slate-500";
  const impactVisible = !loading && impactPercent !== undefined;
  const impactChipLabel = formatDraftImpactChipLabel(impactPercent);

  return (
    <div
      className={`relative min-w-0 rounded-lg border px-3 py-2 ${cardClassName}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={`text-[11px] font-bold uppercase tracking-normal ${labelClassName}`}>
          {tooltipTitle}
        </p>
      </div>
      {loading ? (
        <MetricLoadingState />
      ) : (
        <>
          <div className="mt-1 flex min-h-[28px] items-center justify-between gap-3">
            <p className="text-lg font-semibold text-slate-950">
              {primaryMetric.value}
            </p>
            {secondaryMetric ? (
              <p className={`text-xs font-semibold ${secondaryClassName}`}>
                {secondaryMetric.value}
              </p>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-slate-500">{primaryMetric.label}</p>
        </>
      )}
      {impactVisible && impactChipLabel ? (
        <span
          className={`mt-2 inline-block max-w-full rounded-md border px-2 py-0.5 text-[11px] font-semibold leading-5 ${
            highlight
              ? "border-orange-100 bg-white/80 text-orange-700"
              : "border-slate-200 bg-white text-slate-600"
          }`}
        >
          {impactChipLabel}
        </span>
      ) : null}

    </div>
  );
}

function TargetChoiceCard({ option, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700 ${
        selected
          ? "border-violet-700 bg-violet-50"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
            selected ? "border-violet-700" : "border-slate-400"
          }`}
        >
          {selected ? (
            <div className="h-2.5 w-2.5 rounded-full bg-violet-700" />
          ) : null}
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">{option.title}</p>
          <p className="mt-1 text-sm text-slate-500">{option.description}</p>
        </div>
      </div>
    </button>
  );
}

function DynamicAdjustmentSlider({ value, onChange }) {
  const sliderValue = clampAdjustmentPercent(value);

  return (
    <div className="mt-5 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            Service level adjustment
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Adjust the labor model baseline between the service levels.
          </p>
        </div>
        <div className="rounded-md bg-violet-50 px-3 py-1 text-sm font-semibold text-violet-700">
          {formatSignedPercent(sliderValue)}
        </div>
      </div>

      <div className="mt-6 px-4 pb-7">
        <Slider
          min={ADJUSTMENT_PERCENT_MIN}
          max={ADJUSTMENT_PERCENT_MAX}
          step={0.5}
          marks={ADJUSTMENT_PERCENT_MARKS}
          value={sliderValue}
          valueLabelDisplay="auto"
          valueLabelFormat={formatSignedPercent}
          onChange={(_, nextValue) => onChange(String(nextValue))}
          sx={{
            color: "#6d28d9",
            "& .MuiSlider-thumb": {
              height: 18,
              width: 18,
            },
            "& .MuiSlider-markLabel": {
              color: "#64748b",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            },
          }}
        />
      </div>

      <p className="mt-1 text-xs leading-5 text-slate-500">
        Standard uses the labor model baseline. Higher-touch values add hours;
        leaner values reduce hours.
      </p>
    </div>
  );
}

function TimeframeSelector({ selectedTimeframe, onTimeframeChange }) {
  return (
    <div className="flex h-10 items-center gap-2 text-sm font-semibold text-slate-500">
      {TIMEFRAME_OPTIONS.map((option) => {
        const isSelected = selectedTimeframe === option;

        return (
          <button
            key={option}
            type="button"
            className={`h-10 rounded-lg px-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700 ${
              isSelected
                ? "bg-violet-100 text-violet-700"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            }`}
            onClick={() => onTimeframeChange?.(option)}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

function MetricDetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-8">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function MetricTooltipMetrics({ children }) {
  if (!React.Children.count(children)) {
    return null;
  }

  return (
    <div className="mt-4 border-t border-slate-200 pt-0">
      {children}
    </div>
  );
}

function MetricInfoTooltip({
  ariaLabel = "Show metric details",
  iconClassName = "text-slate-400",
  iconSize = 14,
  tooltipClassName,
  children,
}) {
  const stopTooltipEvent = (event) => {
    event.stopPropagation();
  };
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <div
      className="group inline-flex shrink-0 cursor-help"
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={stopTooltipEvent}
      onMouseDown={stopTooltipEvent}
      onKeyDown={handleKeyDown}
    >
      <InfoOutlinedIcon
        className={`shrink-0 ${iconClassName}`}
        sx={{ fontSize: iconSize }}
      />
      <div
        className={`invisible absolute z-30 max-h-[calc(100vh-96px)] max-w-[calc(100vw-48px)] overflow-y-auto rounded-lg border border-slate-200 bg-white p-5 text-left opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 ${tooltipClassName}`}
        onClick={stopTooltipEvent}
        onMouseDown={stopTooltipEvent}
      >
        {children}
      </div>
    </div>
  );
}

function MetricDetailSection({ label, metric, salesDayCount, windowLabel }) {
  return (
    <div className="mt-5">
      <p className="text-xs font-bold uppercase tracking-normal text-violet-700">
        {formatLabelWithWindow(label, windowLabel)}
      </p>
      <dl className="mt-2 space-y-2 text-sm">
        <MetricDetailRow
          label="Labor Percent"
          value={formatPercent(metric?.labor_percent)}
        />
        <MetricDetailRow label="SPLH" value={formatSplh(metric?.splh)} />
        <MetricDetailRow
          label={salesDayCount ? "Daily Labor Cost" : "Labor Cost"}
          value={formatAverageDailyCurrency(metric?.labor_cost, salesDayCount)}
        />
        <MetricDetailRow
          label={getAverageDailySalesLabel(label, salesDayCount)}
          value={formatCurrency(
            getAverageDailySalesValue(metric?.sales, salesDayCount)
          )}
        />
        <MetricDetailRow
          label={salesDayCount ? "Daily Hours" : "Hours"}
          value={formatAverageDailyHours(metric?.hours, salesDayCount)}
        />
      </dl>
    </div>
  );
}

function getSummaryPrimaryMetric(metric, laborPercentEnabled) {
  if (laborPercentEnabled) {
    return {
      label: "Labor %",
      value: formatPercent(metric?.labor_percent),
      rawValue: getNumber(metric?.labor_percent),
    };
  }

  return {
    label: "SPLH",
    value: formatSplh(metric?.splh),
    rawValue: getNumber(metric?.splh),
  };
}

function getSummarySecondaryMetric(metric, laborPercentEnabled) {
  if (!laborPercentEnabled) {
    return null;
  }

  return {
    label: "SPLH",
    value: formatSplh(metric?.splh),
  };
}

function getLaborUsageStatement(
  actualMetric,
  comparisonMetric,
  comparisonLabel,
  laborPercentEnabled
) {
  const actualValue = getNumber(
    laborPercentEnabled ? actualMetric?.labor_percent : actualMetric?.splh
  );
  const comparisonValue = getNumber(
    laborPercentEnabled
      ? comparisonMetric?.labor_percent
      : comparisonMetric?.splh
  );

  if (actualValue === null || comparisonValue === null || comparisonValue <= 0) {
    return {
      prefix: "Not enough data to compare labor.",
      emphasis: "",
      suffix: "",
      tone: "muted",
    };
  }

  const deltaPercent = laborPercentEnabled
    ? ((actualValue - comparisonValue) / comparisonValue) * 100
    : ((comparisonValue / actualValue) - 1) * 100;
  const isOver = deltaPercent > 0;

  return {
    prefix: "Actual is ",
    emphasis: formatPercent(Math.abs(deltaPercent)),
    suffix: ` hours ${isOver ? "over" : "under"} ${comparisonLabel}.`,
    tone: isOver ? "danger" : "success",
  };
}

function getPlanImpactStatement(
  targetMetric,
  actualMetric,
  label,
  laborPercentEnabled
) {
  const laborPercentDelta = getLaborPlanPercentDelta(
    targetMetric,
    actualMetric,
    laborPercentEnabled
  );

  if (laborPercentDelta === null) {
    return {
      prefix: "Not enough data to compare plan impact.",
      emphasis: "",
      suffix: "",
      tone: "muted",
    };
  }

  return {
    prefix: "Actual is ",
    emphasis: formatPercent(Math.abs(laborPercentDelta)),
    suffix: ` hours ${laborPercentDelta > 0 ? "under" : "over"} ${label}.`,
    tone: getPercentDeltaTone(laborPercentDelta),
  };
}

function getDraftVsForecastedPlanImpactStatement(
  draftMetric,
  forecastedMetric,
  comparisonLabel = "forecasted plan"
) {
  const hoursPercentDelta = getHoursPercentDelta(draftMetric, forecastedMetric);

  if (hoursPercentDelta === null) {
    return {
      prefix: `Not enough data to compare draft to ${comparisonLabel}`,
      emphasis: "",
      suffix: "",
      tone: "muted",
      className: "border-slate-200 bg-white text-slate-600",
    };
  }

  if (Math.abs(hoursPercentDelta) < 0.05) {
    return {
      prefix: `Draft does not change ${comparisonLabel}`,
      emphasis: "",
      suffix: "",
      tone: "muted",
      className: "border-slate-200 bg-white text-slate-600",
    };
  }

  const draftOverPlan = hoursPercentDelta > 0;
  return {
    prefix: `Draft ${draftOverPlan ? "increases" : "decreases"} ${comparisonLabel} by `,
    emphasis: formatPercent(Math.abs(hoursPercentDelta)),
    suffix: "",
    className: draftOverPlan
      ? "border-rose-100 bg-rose-50/80 text-slate-600"
      : "border-emerald-100 bg-emerald-50/80 text-slate-600",
    emphasisClassName:
      draftOverPlan ? "font-semibold text-rose-700" : "font-semibold text-emerald-700",
  };
}

function getPlanUsageStatement(targetMetric, actualMetric, laborPercentEnabled, label) {
  const actualLaborPercent = getNumber(actualMetric?.labor_percent);
  const targetLaborPercent = getNumber(targetMetric?.labor_percent);
  const actualSplh = getNumber(actualMetric?.splh);
  const targetSplh = getNumber(targetMetric?.splh);

  let laborDeltaPercent = null;
  if (
    laborPercentEnabled &&
    actualLaborPercent !== null &&
    targetLaborPercent !== null &&
    actualLaborPercent > 0
  ) {
    laborDeltaPercent =
      ((targetLaborPercent - actualLaborPercent) / actualLaborPercent) * 100;
  } else if (actualSplh !== null && targetSplh !== null && targetSplh > 0) {
    laborDeltaPercent = (actualSplh / targetSplh - 1) * 100;
  }

  if (laborDeltaPercent === null) {
    return {
      prefix: "Not enough data to compare plan to recent actual.",
      emphasis: "",
      suffix: "",
      tone: "muted",
    };
  }

  const actualUnderTarget = laborDeltaPercent > 0;

  return {
    prefix: "Actual is ",
    emphasis: formatPercent(Math.abs(laborDeltaPercent)),
    suffix: ` hours ${actualUnderTarget ? "under" : "over"} ${label}.`,
    tone: actualUnderTarget ? "success" : "danger",
  };
}

function getCaptionToneClass(tone) {
  if (tone === "danger") {
    return "text-rose-700";
  }
  if (tone === "success") {
    return "text-emerald-700";
  }
  return "text-slate-500";
}

function getAggregateAverageWage(aggregate) {
  const cost = getNumber(aggregate?.actual?.labor_cost);
  const hours = getNumber(aggregate?.actual?.hours);
  return cost && hours ? cost / hours : null;
}

function buildSummedMetricGroup(rows, metricKey) {
  const totals = {
    hours: 0,
    sales: 0,
    labor_cost: 0,
  };
  const counts = {
    hours: 0,
    sales: 0,
    labor_cost: 0,
  };

  rows.forEach((row) => {
    Object.keys(totals).forEach((key) => {
      const value = getNumber(row?.[metricKey]?.[key]);
      if (value !== null) {
        totals[key] += value;
        counts[key] += 1;
      }
    });
  });

  return buildMetricFromHours(
    counts.hours > 0 ? totals.hours : null,
    counts.sales > 0 ? totals.sales : null,
    counts.labor_cost > 0 ? totals.labor_cost : null,
    null
  );
}

function buildAggregateFromPlanRows(rows) {
  return {
    actual: buildSummedMetricGroup(rows, "actual"),
    earned: buildSummedMetricGroup(rows, "earned"),
    planned: buildSummedMetricGroup(rows, "planned"),
    forecasted: buildSummedMetricGroup(rows, "forecasted"),
  };
}

function buildAggregatePlannedMetric(aggregate) {
  const plannedMetric = buildMetricFromHours(
    aggregate?.planned?.hours,
    aggregate?.planned?.sales ?? aggregate?.forecasted?.sales,
    aggregate?.planned?.labor_cost,
    getAggregateAverageWage(aggregate)
  );

  return {
    ...plannedMetric,
    splh: plannedMetric.splh ?? aggregate?.planned?.splh ?? null,
  };
}

function buildAggregateScenarioMetric(aggregate, scenarios) {
  const plannedMetric = buildAggregatePlannedMetric(aggregate);
  const totalDeltaHours = (scenarios || []).reduce(
    (total, scenario) =>
      total +
      (getNumber(scenario?.preview?.deltaHours) ??
        getNumber(scenario?.deltaHours) ??
        0),
    0
  );
  const totalCostDelta = (scenarios || []).reduce(
    (total, scenario) =>
      total + (getNumber(scenario?.preview?.costDelta) || 0),
    0
  );
  const scenarioHours =
    getNumber(plannedMetric.hours) !== null
      ? plannedMetric.hours + totalDeltaHours
      : null;
  const scenarioLaborCost =
    getNumber(plannedMetric.labor_cost) !== null
      ? getNumber(plannedMetric.labor_cost) + totalCostDelta
      : null;

  return buildMetricFromHours(
    scenarioHours,
    plannedMetric.sales ?? aggregate?.forecasted?.sales,
    scenarioLaborCost,
    getAggregateAverageWage(aggregate)
  );
}

function LaborPlanSummaryCard({
  eyebrow,
  title,
  dateRange,
  dateRangeLabel = "Date Window",
  storeCount,
  primaryMetric,
  secondaryMetric,
  caption,
  captionStatements,
  detailTitle,
  detailDescription,
  tone = "neutral",
  tooltipAlign = "left",
  children,
}) {
  const isOrangeTone = tone === "orange";
  const cardClassName = isOrangeTone
    ? "border-orange-200 bg-orange-50/70"
    : "border-slate-200 bg-white";
  const eyebrowClassName = isOrangeTone ? "text-orange-700" : "text-slate-500";
  const iconClassName = isOrangeTone ? "text-orange-400" : "text-slate-400";
  const secondaryClassName = isOrangeTone ? "bg-white/70" : "bg-slate-50";
  const visibleCaptions = (captionStatements || [caption]).filter(Boolean);

  return (
    <div className={`relative min-w-0 rounded-lg border p-4 ${cardClassName}`}>
      <div className="flex items-start justify-between gap-3">
        <p
          className={`min-w-0 text-[11px] font-bold uppercase tracking-normal ${eyebrowClassName}`}
        >
          {eyebrow}
        </p>
        <MetricInfoTooltip
          ariaLabel={`Show details for ${title}`}
          iconClassName={iconClassName}
          iconSize={18}
          tooltipClassName={`top-[calc(100%+8px)] w-[560px] ${
            tooltipAlign === "right" ? "right-0" : "left-0"
          }`}
        >
          <h3 className="text-base font-semibold text-slate-900">{detailTitle}</h3>
          {detailDescription ? (
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {detailDescription}
            </p>
          ) : null}
          <div className="mt-4 border-t border-slate-200 pt-4">
            <dl className="space-y-2 text-sm">
              {dateRange ? (
                <MetricDetailRow label={dateRangeLabel} value={dateRange} />
              ) : null}
              {storeCount !== undefined ? (
                <MetricDetailRow
                  label="Stores Included"
                  value={formatStoreCount(storeCount)}
                />
              ) : null}
            </dl>
          </div>
          <MetricTooltipMetrics>{children}</MetricTooltipMetrics>
        </MetricInfoTooltip>
      </div>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-3xl font-semibold tracking-tight text-slate-950">
            {primaryMetric.value}
          </p>
          <p className="mt-1 text-sm text-slate-500">{primaryMetric.label}</p>
        </div>
        {secondaryMetric ? (
          <div className={`rounded-md px-2.5 py-1.5 text-right ${secondaryClassName}`}>
            <p className="text-base font-semibold text-slate-900">
              {secondaryMetric.value}
            </p>
            <p className="mt-0.5 text-sm text-slate-500">
              {secondaryMetric.label}
            </p>
          </div>
        ) : null}
      </div>

      {visibleCaptions.length ? (
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-medium leading-6 text-slate-500">
          {visibleCaptions.map((visibleCaption, index) => (
            <p key={`${visibleCaption.prefix || visibleCaption}-${index}`}>
              <ImpactStatementText statement={visibleCaption} />
            </p>
          ))}
        </div>
      ) : null}

    </div>
  );
}

function LaborPlanMetricSummary({
  aggregate,
  laborPercentEnabled,
  draftScenarios = [],
  windowLabels,
  windowDayCounts,
  storeCount,
}) {
  const actualMetric = aggregate?.actual || {};
  const earnedMetric = aggregate?.earned || {};
  const plannedMetric = buildAggregatePlannedMetric(aggregate);
  const hasDraftScenario = draftScenarios.length > 0;
  const targetMetric = hasDraftScenario
    ? buildAggregateScenarioMetric(aggregate, draftScenarios)
    : plannedMetric;
  const actualPrimaryMetric = getSummaryPrimaryMetric(
    actualMetric,
    laborPercentEnabled
  );
  const actualSecondaryMetric = getSummarySecondaryMetric(
    actualMetric,
    laborPercentEnabled
  );
  const plannedPrimaryMetric = getSummaryPrimaryMetric(
    targetMetric,
    laborPercentEnabled
  );
  const plannedSecondaryMetric = getSummarySecondaryMetric(
    targetMetric,
    laborPercentEnabled
  );
  const actualUsageStatement = getLaborUsageStatement(
    actualMetric,
    earnedMetric,
    "earned",
    laborPercentEnabled
  );
  const targetImpactStatement = hasDraftScenario
    ? getPlanImpactStatement(
        targetMetric,
        actualMetric,
        "draft forecast",
        laborPercentEnabled
      )
    : getPlanUsageStatement(
        targetMetric,
        actualMetric,
        laborPercentEnabled,
        "forecasted plan"
      );
  const draftVsForecastedPlanStatement = hasDraftScenario
    ? getDraftVsForecastedPlanImpactStatement(
        targetMetric,
        plannedMetric,
        "current forecast"
      )
    : null;
  const companyCaptionDraftVsForecastedPlanStatement =
    hasDraftScenario
      ? {
          ...getDraftVsForecastedPlanImpactStatement(
            targetMetric,
            plannedMetric,
            "company forecast"
          ),
          suffix: ".",
        }
      : null;
  const actualCardTitle = windowLabels?.historical
    ? `Company recent actual (${windowLabels.historical})`
    : "Company recent actual";
  const forecastCardTitle = windowLabels?.forecast
    ? `Company ${hasDraftScenario ? "draft forecast" : "forecasted plan"} (${windowLabels.forecast})`
    : hasDraftScenario
      ? "Company draft forecast"
      : "Company forecasted plan";
  const historicalWindowLabel = windowLabels?.historical;
  const forecastWindowLabel = windowLabels?.forecast;
  const historicalSalesDayCount = windowDayCounts?.historical;
  const forecastSalesDayCount = windowDayCounts?.forecast;
  return (
    <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-1">
      <LaborPlanSummaryCard
        eyebrow="Recent actual"
        title={actualCardTitle}
        storeCount={storeCount}
        primaryMetric={actualPrimaryMetric}
        secondaryMetric={actualSecondaryMetric}
        caption={actualUsageStatement}
        detailTitle={actualCardTitle}
        detailDescription="Recent actual is what was used. Earned labor is what the labor model would have targeted from actual sales on those same days."
      >
        <MetricDetailSection
          label="Company recent actual"
          metric={actualMetric}
          salesDayCount={historicalSalesDayCount}
          windowLabel={historicalWindowLabel}
        />
        <MetricDetailSection
          label="Company earned labor"
          metric={earnedMetric}
          salesDayCount={historicalSalesDayCount}
          windowLabel={historicalWindowLabel}
        />
        <MetricImpactStatementBlock statement={actualUsageStatement} />
      </LaborPlanSummaryCard>

      <LaborPlanSummaryCard
        eyebrow={hasDraftScenario ? "Draft forecast" : "Forecasted plan"}
        title={forecastCardTitle}
        storeCount={storeCount}
        primaryMetric={plannedPrimaryMetric}
        secondaryMetric={plannedSecondaryMetric}
        caption={targetImpactStatement}
        captionStatements={
          hasDraftScenario
            ? [targetImpactStatement, companyCaptionDraftVsForecastedPlanStatement]
            : undefined
        }
        detailTitle={forecastCardTitle}
        detailDescription="Forecasted plan uses forecasted sales, the labor model baseline, and any store-level adjustments or fixed targets."
        tone={hasDraftScenario ? "orange" : "neutral"}
      >
        <MetricDetailSection
          label={
            hasDraftScenario
              ? "Company draft forecast"
              : "Company forecasted plan"
          }
          metric={targetMetric}
          salesDayCount={forecastSalesDayCount}
          windowLabel={forecastWindowLabel}
        />
        {hasDraftScenario ? (
          <MetricDetailSection
            label="Company current forecast"
            metric={plannedMetric}
            salesDayCount={forecastSalesDayCount}
            windowLabel={forecastWindowLabel}
          />
        ) : null}
        <MetricDetailSection
          label="Company recent actual"
          metric={actualMetric}
          salesDayCount={historicalSalesDayCount}
          windowLabel={historicalWindowLabel}
        />
        <MetricImpactStatementBlock
          statements={[targetImpactStatement, draftVsForecastedPlanStatement]}
        />
      </LaborPlanSummaryCard>
    </div>
  );
}

function StoreMetricPill({
  label,
  metric,
  laborPercentEnabled,
  detailTitle,
  description,
  dateRange,
  dateRangeLabel = "Date Window",
  metricWindowLabel,
  storeCount,
  salesDayCount,
  extraMetricSections = [],
  detailRows = [],
  footer,
  visibleFooterStatements = [],
  extraFooterStatements = [],
  highlight = false,
  tooltipAlign = "left",
  loading = false,
}) {
  const primaryMetric = getSummaryPrimaryMetric(metric, laborPercentEnabled);
  const secondaryMetric = getSummarySecondaryMetric(metric, laborPercentEnabled);
  const cardClassName = highlight
    ? "border-orange-200 bg-orange-50/70 shadow-sm"
    : "border-slate-200 bg-white shadow-sm";
  const labelClassName = highlight ? "text-orange-700" : "text-slate-500";
  const secondaryClassName = highlight ? "text-orange-700" : "text-slate-500";
  const visibleFooters = loading
    ? []
    : [footer, ...visibleFooterStatements].filter(Boolean);

  return (
    <div
      className={`relative min-w-[150px] rounded-lg border px-3 py-2 transition-colors ${cardClassName}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={`text-[11px] font-bold uppercase tracking-normal ${labelClassName}`}>
          {label}
        </p>
        <MetricInfoTooltip
          ariaLabel={`Show details for ${detailTitle}`}
          iconSize={14}
          tooltipClassName={`top-[calc(100%+8px)] w-[480px] ${
            tooltipAlign === "right" ? "right-0" : "left-0"
          }`}
        >
          <h3 className="text-base font-semibold text-slate-900">{detailTitle}</h3>
          {description ? (
            <p className="mt-1 text-xs text-slate-500">{description}</p>
          ) : null}
          <div className="mt-4 border-t border-slate-200 pt-4">
            <dl className="space-y-2 text-sm">
              {dateRange ? (
                <MetricDetailRow label={dateRangeLabel} value={dateRange} />
              ) : null}
              {storeCount !== undefined ? (
                <MetricDetailRow
                  label="Stores Included"
                  value={formatStoreCount(storeCount)}
                />
              ) : null}
            </dl>
          </div>
          <MetricTooltipMetrics>
            <MetricDetailSection
              label={label}
              metric={metric}
              salesDayCount={salesDayCount}
              windowLabel={metricWindowLabel}
            />
            {extraMetricSections.map((section) => (
              <MetricDetailSection
                key={section.label}
                label={section.label}
                metric={section.metric}
                salesDayCount={section.salesDayCount ?? salesDayCount}
                windowLabel={section.windowLabel ?? metricWindowLabel}
              />
            ))}
            <MetricImpactStatementBlock
              statements={[footer, ...extraFooterStatements]}
            />
            {[...detailRows, ...(footer?.detailRows || [])].length ? (
              <div className="mt-5 border-t border-slate-200 pt-4">
                <dl className="space-y-2 text-sm">
                  {[...detailRows, ...(footer?.detailRows || [])].map((row) => (
                    <MetricDetailRow
                      key={row.label}
                      label={row.label}
                      value={row.value}
                    />
                  ))}
                </dl>
              </div>
            ) : null}
          </MetricTooltipMetrics>
        </MetricInfoTooltip>
      </div>
      {loading ? (
        <MetricLoadingState />
      ) : (
        <>
          <div className="mt-3 flex min-h-[30px] items-center justify-between gap-3">
            <p className="text-2xl font-semibold tracking-tight text-slate-950">
              {primaryMetric.value}
            </p>
            {secondaryMetric ? (
              <p className={`text-base font-semibold ${secondaryClassName}`}>
                {secondaryMetric.value}
              </p>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-slate-500">{primaryMetric.label}</p>
        </>
      )}
      {visibleFooters.length ? (
        <div className="mt-4 space-y-1 border-t border-slate-100 pt-3">
          {visibleFooters.map((visibleFooter, index) => (
            <p
              key={`${visibleFooter.prefix || visibleFooter}-${index}`}
              className="text-sm font-medium leading-5 text-slate-600"
            >
              <ImpactStatementText statement={visibleFooter} />
            </p>
          ))}
        </div>
      ) : null}

    </div>
  );
}

export function StoreTrendSparkline({
  seriesRows,
  scenarioSeriesRows,
  scenarioLabel = "Draft",
  visibleSeries = {},
  consolidatePublishedSeries = false,
  dense = false,
}) {
  const [activeIndex, setActiveIndex] = React.useState(null);
  const isSeriesVisible = (seriesKey) => visibleSeries[seriesKey] !== false;
  const hasScenarioSeries =
    Array.isArray(scenarioSeriesRows) &&
    scenarioSeriesRows.some((row) => getNumber(row?.scenario?.hours) !== null);
  const showPublishedSeries = consolidatePublishedSeries && hasScenarioSeries;
  const showEarnedSeries = !showPublishedSeries;
  const showForecastedSeries = !showPublishedSeries;
  const chartRows = Array.isArray(seriesRows) ? seriesRows : [];
  const width = 340;
  const height = dense ? 60 : 74;
  const padding = dense
    ? { top: 5, right: 8, bottom: 6, left: 8 }
    : { top: 6, right: 8, bottom: 8, left: 8 };
  const maxHours = Math.max(
    ...chartRows.flatMap((row, index) => [
      getLaborPlanChartValue(row, "actual") || 0,
      getLaborPlanChartValue(row, "earned") || 0,
      getLaborPlanChartValue(row, "forecasted") || 0,
      getLaborPlanChartValue(row, "published") || 0,
      hasScenarioSeries
        ? getLaborPlanChartValue(scenarioSeriesRows[index], "scenario") || 0
        : 0,
    ]),
    1
  );
  const hasData = chartRows.some((row) => {
    return (
      (getLaborPlanChartValue(row, "actual") || 0) > 0 ||
      (getLaborPlanChartValue(row, "earned") || 0) > 0 ||
      (getLaborPlanChartValue(row, "forecasted") || 0) > 0
    );
  });
  const actualPoints = getChartPoints(
    chartRows,
    (row) => getLaborPlanChartValue(row, "actual"),
    width,
    height,
    padding,
    maxHours
  );
  const earnedPoints = getChartPoints(
    chartRows,
    (row) => getLaborPlanChartValue(row, "earned"),
    width,
    height,
    padding,
    maxHours
  );
  const forecastedPoints = getChartPoints(
    chartRows,
    (row) => getLaborPlanChartValue(row, "forecasted"),
    width,
    height,
    padding,
    maxHours
  );
  const publishedPoints = showPublishedSeries
    ? getChartPoints(
        chartRows,
        (row) => getLaborPlanChartValue(row, "published"),
        width,
        height,
        padding,
        maxHours
      )
    : [];
  const scenarioPoints = hasScenarioSeries
    ? getChartPoints(
        scenarioSeriesRows,
        (row) => getLaborPlanChartValue(row, "scenario"),
        width,
        height,
        padding,
        maxHours
      )
    : [];
  const actualPath = getChartPathFromPoints(actualPoints);
  const earnedPath = getChartPathFromPoints(earnedPoints);
  const forecastedPath = getChartPathFromPoints(forecastedPoints);
  const publishedPath = getChartPathFromPoints(publishedPoints);
  const scenarioPath = getChartPathFromPoints(scenarioPoints);
  const forecastStartIndex = chartRows.findIndex(
    (row) => row?.period === "forecast"
  );
  const forecastStartX =
    forecastStartIndex >= 0
      ? padding.left +
        (forecastStartIndex / Math.max(chartRows.length - 1, 1)) *
          (width - padding.left - padding.right)
      : null;
  const activeRow =
    activeIndex === null ? null : chartRows[activeIndex] || null;
  const activeActualPoint =
    activeIndex === null ? null : actualPoints[activeIndex] || null;
  const activeEarnedPoint =
    activeIndex === null ? null : earnedPoints[activeIndex] || null;
  const activeForecastedPoint =
    activeIndex === null ? null : forecastedPoints[activeIndex] || null;
  const activePublishedPoint =
    activeIndex === null ? null : publishedPoints[activeIndex] || null;
  const activeScenarioPoint =
    activeIndex === null ? null : scenarioPoints[activeIndex] || null;
  const activeValuePoints = [
    isSeriesVisible("actual") ? activeActualPoint : null,
    showEarnedSeries && isSeriesVisible("earned") ? activeEarnedPoint : null,
    showForecastedSeries && isSeriesVisible("forecasted") ? activeForecastedPoint : null,
    showPublishedSeries && isSeriesVisible("published") ? activePublishedPoint : null,
    isSeriesVisible("scenario") ? activeScenarioPoint : null,
  ].filter(hasChartPointValue);
  const activeAnchorPoint =
    activeValuePoints[0] ||
    (activeIndex === null ? null : actualPoints[activeIndex] || null);
  const activeHistoricalVariance = showPublishedSeries
    ? isSeriesVisible("scenario") &&
      isSeriesVisible("actual") &&
      hasChartPointValue(activeScenarioPoint) &&
      hasChartPointValue(activeActualPoint)
        ? activeScenarioPoint.rawValue - activeActualPoint.rawValue
        : null
    : isSeriesVisible("actual") &&
        isSeriesVisible("earned") &&
        hasChartPointValue(activeActualPoint) &&
        hasChartPointValue(activeEarnedPoint)
      ? activeActualPoint.rawValue - activeEarnedPoint.rawValue
      : null;
  const activeScenarioComparisonPoint =
    showPublishedSeries
      ? activePublishedPoint
      : activeRow?.period === "historical"
        ? activeEarnedPoint
        : activeForecastedPoint;
  const activeScenarioVariance =
    isSeriesVisible("scenario") &&
    (showPublishedSeries
      ? isSeriesVisible("published")
      : isSeriesVisible(
          activeRow?.period === "historical" ? "earned" : "forecasted"
        )) &&
    hasChartPointValue(activeScenarioPoint) &&
    hasChartPointValue(activeScenarioComparisonPoint)
      ? activeScenarioPoint.rawValue - activeScenarioComparisonPoint.rawValue
      : null;
  const activeSalesMetric = getLaborPlanChartSalesMetric(activeRow);
  const tooltipLeftPercent = activeAnchorPoint
    ? (activeAnchorPoint.x / width) * 100
    : 0;
  const tooltipLeft = `clamp(0px, calc(${tooltipLeftPercent}% - ${
    STORE_TREND_TOOLTIP_WIDTH / 2
  }px), calc(100% - ${STORE_TREND_TOOLTIP_WIDTH}px))`;

  function handleSparklineMouseMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const usableWidth = width - padding.left - padding.right;
    const rawIndex = Math.round(
      ((x - padding.left) / usableWidth) * (chartRows.length - 1)
    );
    const nextIndex = Math.max(0, Math.min(chartRows.length - 1, rawIndex));
    setActiveIndex(nextIndex);
  }

  if (chartRows.length < 2 || !hasData) {
    return (
      <div
        className={`flex w-full min-w-0 max-w-[420px] flex-1 items-center justify-center rounded-md border border-dashed border-slate-200 text-xs text-slate-400 ${
          dense ? "h-[60px]" : "h-[74px]"
        }`}
      >
        No trend data
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-[420px] flex-1">
      <div className="relative">
        <svg
          className={`w-full overflow-visible ${
            dense ? "h-[60px]" : "h-[74px]"
          }`}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Store labor trend"
          preserveAspectRatio="none"
          onMouseMove={handleSparklineMouseMove}
          onMouseLeave={() => setActiveIndex(null)}
        >
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={height - padding.bottom}
            y2={height - padding.bottom}
            stroke="#e2e8f0"
            strokeDasharray="3 4"
            vectorEffect="non-scaling-stroke"
          />
          {forecastStartX !== null ? (
            <line
              x1={forecastStartX}
              x2={forecastStartX}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {isSeriesVisible("actual") ? (
            <path
              d={actualPath}
              fill="none"
              stroke="#4f46e5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.75"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {showEarnedSeries && isSeriesVisible("earned") ? (
            <path
              d={earnedPath}
              fill="none"
              stroke="#8b5cf6"
              strokeDasharray="4 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.75"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {showForecastedSeries && isSeriesVisible("forecasted") ? (
            <path
              d={forecastedPath}
              fill="none"
              stroke="#6d28d9"
              strokeDasharray="2 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.75"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {showPublishedSeries && isSeriesVisible("published") ? (
            <path
              d={publishedPath}
              fill="none"
              stroke="#8b5cf6"
              strokeDasharray="4 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.75"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {hasScenarioSeries && isSeriesVisible("scenario") ? (
            <path
              d={scenarioPath}
              fill="none"
              stroke="#ea580c"
              strokeDasharray="5 4"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.75"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {activeAnchorPoint ? (
            <line
              x1={activeAnchorPoint.x}
              x2={activeAnchorPoint.x}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#94a3b8"
              strokeDasharray="3 4"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {isSeriesVisible("actual") && hasChartPointValue(activeActualPoint) ? (
            <circle
              cx={activeActualPoint.x}
              cy={activeActualPoint.y}
              r="3"
              fill="#4f46e5"
              stroke="#ffffff"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {showEarnedSeries && isSeriesVisible("earned") && hasChartPointValue(activeEarnedPoint) ? (
            <circle
              cx={activeEarnedPoint.x}
              cy={activeEarnedPoint.y}
              r="3"
              fill="#ffffff"
              stroke="#8b5cf6"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {showForecastedSeries && isSeriesVisible("forecasted") && hasChartPointValue(activeForecastedPoint) ? (
            <circle
              cx={activeForecastedPoint.x}
              cy={activeForecastedPoint.y}
              r="3"
              fill="#ffffff"
              stroke="#6d28d9"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {showPublishedSeries && isSeriesVisible("published") && hasChartPointValue(activePublishedPoint) ? (
            <circle
              cx={activePublishedPoint.x}
              cy={activePublishedPoint.y}
              r="3"
              fill="#ffffff"
              stroke="#8b5cf6"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          {isSeriesVisible("scenario") && hasChartPointValue(activeScenarioPoint) ? (
            <circle
              cx={activeScenarioPoint.x}
              cy={activeScenarioPoint.y}
              r="3"
              fill="#ffffff"
              stroke="#ea580c"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
          <rect
            x={padding.left}
            y={padding.top}
            width={width - padding.left - padding.right}
            height={height - padding.top - padding.bottom}
            fill="transparent"
            pointerEvents="all"
          />
        </svg>

        {activeAnchorPoint && activeRow ? (
          <div
            className="pointer-events-none absolute top-0 z-30 w-[280px] rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-xl"
            style={{
              left: tooltipLeft,
              transform: "translateY(calc(-100% - 6px))",
            }}
          >
            <p className="font-semibold uppercase tracking-normal text-slate-500">
              {formatShortDate(activeRow?.date)}
            </p>
            <div className="mt-2 space-y-1.5">
              {activeSalesMetric ? (
                <MetricDetailRow
                  label={activeSalesMetric.label}
                  value={activeSalesMetric.value}
                />
              ) : null}
              {isSeriesVisible("actual") && hasChartPointValue(activeActualPoint) ? (
                <MetricDetailRow
                  label="Actual Labor"
                  value={`${formatFixedNumber(activeActualPoint.rawValue)} hrs`}
                />
              ) : null}
              {showEarnedSeries && isSeriesVisible("earned") && hasChartPointValue(activeEarnedPoint) ? (
                <MetricDetailRow
                  label="Earned Labor"
                  value={`${formatFixedNumber(activeEarnedPoint.rawValue)} hrs`}
                />
              ) : null}
              {showForecastedSeries && isSeriesVisible("forecasted") && hasChartPointValue(activeForecastedPoint) ? (
                <MetricDetailRow
                  label="Forecasted Labor"
                  value={`${formatFixedNumber(activeForecastedPoint.rawValue)} hrs`}
                />
              ) : null}
              {showPublishedSeries && isSeriesVisible("published") && hasChartPointValue(activePublishedPoint) ? (
                <MetricDetailRow
                  label="Published Labor"
                  value={`${formatFixedNumber(activePublishedPoint.rawValue)} hrs`}
                />
              ) : null}
              {isSeriesVisible("scenario") && hasChartPointValue(activeScenarioPoint) ? (
                <MetricDetailRow
                  label={`${scenarioLabel} labor`}
                  value={`${formatFixedNumber(activeScenarioPoint.rawValue)} hrs`}
                />
              ) : null}
            </div>
            {activeHistoricalVariance !== null || activeScenarioVariance !== null ? (
              <div className="mt-2 border-t border-slate-200 pt-2">
                {activeHistoricalVariance !== null ? (
                  <MetricDetailRow
                    label={showPublishedSeries ? "Sandbox vs actual used" : "Actual vs earned"}
                    value={formatDeltaHours(activeHistoricalVariance)}
                  />
                ) : null}
                {activeScenarioVariance !== null ? (
                  <MetricDetailRow
                    label={`${scenarioLabel} vs ${
                      showPublishedSeries
                        ? "published"
                        : activeRow?.period === "historical"
                          ? "earned"
                          : "forecast"
                    }`}
                    value={formatDeltaHours(activeScenarioVariance)}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StorePlanTrendLegend({ showDraft }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs font-medium text-slate-500">
      <div className="flex items-center gap-1.5">
        <span className="h-0.5 w-5 bg-violet-700" />
        Actual
      </div>
      <div className="flex items-center gap-1.5">
        <span className="h-0.5 w-5 border-t-2 border-dashed border-violet-500" />
        Earned
      </div>
      <div className="flex items-center gap-1.5">
        <span className="h-0.5 w-5 border-t-2 border-dotted border-violet-700" />
        Forecast
      </div>
      {showDraft ? (
        <div className="flex items-center gap-1.5">
          <span className="h-0.5 w-5 border-t-2 border-dashed border-orange-600" />
          Draft
        </div>
      ) : null}
    </div>
  );
}

export function CompanyStoreSelector({
  stores,
  selectedStoreIds,
  onChange,
  title = "Company trend stores",
  description = "Choose stores included in the cards, trend, and store plans.",
  menuAlign = "left",
}) {
  const [open, setOpen] = React.useState(false);
  const selectorRef = React.useRef(null);
  const allStoreIds = stores.map((store) => String(store.id));
  const selectedSet = React.useMemo(
    () => new Set(selectedStoreIds.map(String)),
    [selectedStoreIds]
  );
  const selectedCount = selectedStoreIds.length;
  const allSelected = selectedCount === allStoreIds.length;
  const countLabel = allSelected
    ? `${selectedCount} ${selectedCount === 1 ? "store" : "stores"}`
    : `${selectedCount} of ${allStoreIds.length} stores`;

  React.useEffect(() => {
    if (!open) return undefined;

    const handleOutsidePointerDown = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [open]);

  function handleToggleStore(storeId) {
    const normalizedStoreId = String(storeId);
    const nextSet = new Set(selectedStoreIds.map(String));

    if (nextSet.has(normalizedStoreId)) {
      if (nextSet.size <= 1) {
        return;
      }
      nextSet.delete(normalizedStoreId);
    } else {
      nextSet.add(normalizedStoreId);
    }

    onChange(Array.from(nextSet));
  }

  function handleSelectAll() {
    onChange(allStoreIds);
  }

  return (
    <div ref={selectorRef} className="relative">
      <button
        type="button"
        className="inline-flex h-10 items-center gap-2 rounded-lg border border-violet-100 bg-violet-100 px-3 text-sm font-semibold text-violet-700 hover:border-violet-200 hover:bg-violet-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
        <span>{countLabel}</span>
      </button>

      {open ? (
        <div
          className={`absolute top-[calc(100%+8px)] z-[120] w-[min(320px,calc(100vw-32px))] rounded-lg border border-slate-200 bg-white shadow-xl ${
            menuAlign === "right" ? "right-0" : "left-0"
          }`}
        >
          <div className="border-b border-slate-200 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {title}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {description}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 text-xs font-semibold text-violet-700 hover:text-violet-900"
                onClick={handleSelectAll}
              >
                All stores
              </button>
            </div>
          </div>

          <div className="max-h-[280px] overflow-auto py-1">
            {stores.map((store) => {
              const storeId = String(store.id);
              const selected = selectedSet.has(storeId);
              return (
                <button
                  key={storeId}
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                  onClick={() => handleToggleStore(storeId)}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-violet-700 accent-violet-700 focus:ring-violet-700"
                    checked={selected}
                    readOnly
                    tabIndex={-1}
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {store.store_name}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="border-t border-slate-200 px-4 py-3 text-right">
            <button
              type="button"
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LaborPlanInfoPanel() {
  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/50 px-5 py-4">
      <p className="text-sm font-semibold text-slate-900">Labor plan overview</p>
      <p className="mt-1 max-w-5xl text-sm leading-6 text-slate-600">
        Use this page to compare recent staffing against earned labor, review the forecasted plan, and make store-specific labor adjustments. Change the company view with the filters, click a store to preview adjustments, then publish changes to apply queued drafts and rerun forecasted labor.
      </p>
    </div>
  );
}

function StoreLaborPlanCard({
  row,
  seriesRows,
  scenario,
  scenarioSeriesRows,
  draft,
  draftLoading,
  draftActive,
  draftValidationMessage,
  windowLabels,
  windowDayCounts,
  onEdit,
}) {
  const editable = Boolean(onEdit);
  const laborPercentEnabled = Boolean(row?.store?.labor_percent_enabled);
  const savedTargetMetric = getTargetMetric(row);
  const savedTargetMetricGroup = {
    hours: savedTargetMetric.hours,
    splh: savedTargetMetric.splh,
    labor_percent: savedTargetMetric.labor_percent,
    sales: savedTargetMetric.sales,
    labor_cost: savedTargetMetric.labor_cost,
  };
  const targetMetric = draftValidationMessage
    ? {
        visibleLabel: "Draft incomplete",
        detail: draftValidationMessage,
        hours: null,
        splh: null,
        labor_percent: null,
      }
    : scenario
      ? {
          visibleLabel: "Draft scenario",
          detail: scenario.targetSummary?.label,
          hours: scenario.metrics?.comparison?.hours,
          splh: scenario.metrics?.comparison?.splh,
          labor_percent: scenario.metrics?.comparison?.labor_percent,
          sales: scenario.metrics?.comparison?.sales,
          labor_cost: scenario.metrics?.comparison?.labor_cost,
        }
      : savedTargetMetric;
  const targetMetricGroup = {
    hours: targetMetric.hours,
    splh: targetMetric.splh,
    labor_percent: targetMetric.labor_percent,
    sales: targetMetric.sales,
    labor_cost: targetMetric.labor_cost,
  };
  const historicalWindowLabel = windowLabels?.historical;
  const forecastWindowLabel = windowLabels?.forecast;
  const historicalSalesDayCount = windowDayCounts?.historical;
  const forecastSalesDayCount = windowDayCounts?.forecast;
  const targetLaborImpactDisplay = getStorePlanLaborImpactDisplay(
    row,
    targetMetricGroup,
    laborPercentEnabled,
    draftActive ? "draft forecast" : "forecasted plan"
  );
  const draftVsForecastedPlanStatement = draftActive
    ? getDraftVsForecastedPlanImpactStatement(
        targetMetricGroup,
        savedTargetMetricGroup,
        "current forecast"
      )
    : null;
  const dynamicAdjustmentDisplay = getDynamicAdjustmentDisplay(row, draft);
  const targetMethodDisplay = getTargetMethodDisplay(
    row,
    draft,
    draftValidationMessage
  );
  const forecastMetricSections = [
    ...(draftActive
      ? [
          {
            label: "Current forecast",
            metric: savedTargetMetricGroup,
            salesDayCount: forecastSalesDayCount,
            windowLabel: forecastWindowLabel,
          },
        ]
      : []),
    {
      label: "Recent actual",
      metric: row.planRow?.actual || {},
      salesDayCount: historicalSalesDayCount,
      windowLabel: historicalWindowLabel,
    },
  ];
  const handleOpenEdit = () => {
    onEdit?.(row.store);
  };
  const handleCardKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenEdit();
    }
  };
  const handleEditButtonClick = (event) => {
    event.stopPropagation();
    handleOpenEdit();
  };
  const handleEditButtonKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  };

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors ${
        editable
          ? "cursor-pointer hover:border-violet-200 hover:bg-violet-50/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
          : ""
      }`}
      role={editable ? "button" : undefined}
      tabIndex={editable ? 0 : undefined}
      onClick={editable ? handleOpenEdit : undefined}
      onKeyDown={editable ? handleCardKeyDown : undefined}
      aria-label={
        editable
          ? `Edit labor plan for ${row.store?.store_name || "store"}`
          : undefined
      }
    >
      <div className="flex items-center gap-4">
        <div className="w-[185px] shrink-0 self-start pt-1">
          <p className="truncate text-base font-semibold text-slate-900">
            {row.store?.store_name}
          </p>
          <TargetMethodBadge display={targetMethodDisplay} />
          <DynamicAdjustmentBadge display={dynamicAdjustmentDisplay} />
        </div>

        <div className="grid w-[440px] shrink-0 grid-cols-2 gap-3">
          <StoreMetricPill
            label="Recent actual"
            metric={row.planRow?.actual || {}}
            laborPercentEnabled={laborPercentEnabled}
            detailTitle="Historical performance"
            description="Recent actual is what was used. Earned labor is what the labor model would have targeted from actual sales on those same days."
            metricWindowLabel={historicalWindowLabel}
            storeCount={1}
            salesDayCount={historicalSalesDayCount}
            extraMetricSections={[
              {
                label: "Earned labor",
                metric: row.planRow?.earned || {},
                salesDayCount: historicalSalesDayCount,
                windowLabel: historicalWindowLabel,
              },
            ]}
            footer={getActualVsEarnedImpactDisplay(row.planRow)}
          />
          <StoreMetricPill
            label={draftActive ? "Draft forecast" : "Forecasted plan"}
            metric={targetMetricGroup}
            laborPercentEnabled={laborPercentEnabled}
            detailTitle={draftActive ? "Draft forecast" : "Forecasted plan"}
            description="Forecasted labor from forecasted sales, the labor model baseline, and store-level overrides."
            metricWindowLabel={forecastWindowLabel}
            storeCount={1}
            salesDayCount={forecastSalesDayCount}
            extraMetricSections={forecastMetricSections}
            footer={targetLaborImpactDisplay}
            visibleFooterStatements={
              draftVsForecastedPlanStatement
                ? [draftVsForecastedPlanStatement]
                : []
            }
            extraFooterStatements={
              draftVsForecastedPlanStatement
                ? [draftVsForecastedPlanStatement]
                : []
            }
            highlight={draftActive}
            loading={draftLoading}
            tooltipAlign="right"
          />
        </div>

        <StoreTrendSparkline
          seriesRows={seriesRows}
          scenarioSeriesRows={scenarioSeriesRows}
        />

        <div className="shrink-0 self-center">
          <button
            type="button"
            className="rounded-lg border border-violet-200 px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
            onClick={handleEditButtonClick}
            onKeyDown={handleEditButtonKeyDown}
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

function LaborPlanDraftPublishBar({
  draftCount,
  invalidDraftCount,
  scenarioSummary,
  draftSummaries = [],
  publishedState,
  forecastWindowLabel,
  onDiscard,
  onPublish,
  publishing,
}) {
  const hasDrafts = draftCount > 0;
  const showPublished = !hasDrafts && Boolean(publishedState);
  const hasInvalidDrafts = hasDrafts && invalidDraftCount > 0;
  const draftStoreNames = draftSummaries.map((draftSummary) => draftSummary.storeName);
  const draftStoreNameList = formatStoreNameList(draftStoreNames);
  const draftImpactDisplay = getDraftCurrentForecastImpactDisplay(
    scenarioSummary.percentDelta
  );
  const summaryText = hasDrafts
    ? hasInvalidDrafts
      ? `${invalidDraftCount} incomplete ${invalidDraftCount === 1 ? "draft" : "drafts"}.`
      : null
    : showPublished
      ? `${publishedState.count} ${publishedState.count === 1 ? "store" : "stores"} updated.`
      : "No pending updates.";
  const publishDescription = forecastWindowLabel
    ? `Applies and reruns forecasted labor for ${forecastWindowLabel}.`
    : "Applies and reruns forecasted labor for the visible forecast horizon.";
  const cardClassName = hasDrafts
    ? "border-orange-200 bg-orange-50/70"
    : showPublished
      ? "border-emerald-200 bg-emerald-50/70"
      : "border-slate-200 bg-slate-50";

  return (
    <div className={`min-h-[72px] rounded-lg border p-4 ${cardClassName}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">
            {hasDrafts
              ? `${draftCount} ${
                  draftCount === 1 ? "store" : "stores"
                } with pending ${draftCount === 1 ? "change" : "changes"}`
              : showPublished
                ? "Published"
                : "Labor plan changes"}
          </p>
          {hasDrafts && !hasInvalidDrafts ? (
            <div className="mt-2 flex max-w-full flex-wrap items-center gap-2">
              {draftStoreNameList ? (
                <span className="inline-flex max-w-full items-center rounded-md border border-orange-100 bg-white/80 px-2 py-1 text-xs font-semibold text-slate-700">
                  <span className="truncate">{draftStoreNameList}</span>
                </span>
              ) : null}
              <span
                className={`inline-flex max-w-full items-center rounded-md border px-2 py-1 text-xs font-semibold ${draftImpactDisplay.className}`}
              >
                <ImpactStatementText statement={draftImpactDisplay} />
              </span>
            </div>
          ) : (
            <p className="mt-1 text-xs text-slate-600">
              {summaryText}
            </p>
          )}
        </div>

        {hasDrafts ? (
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              className="whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
              onClick={onDiscard}
              disabled={publishing}
            >
              Discard
            </button>
            <div className="group relative inline-flex">
              <LoadingButton
                variant="contained"
                loading={publishing}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                onClick={onPublish}
                disabled={hasInvalidDrafts}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  paddingX: "18px",
                  paddingY: "8px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#cbd5e1",
                    color: "#64748b",
                  },
                  fontSize: "14px",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                Publish changes
              </LoadingButton>
              <div className="pointer-events-none invisible absolute right-0 top-[calc(100%+8px)] z-40 w-[240px] rounded-lg border border-slate-200 bg-white p-3 text-left text-xs text-slate-600 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                {publishDescription}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`shrink-0 rounded-lg border bg-white/70 px-4 py-2 text-sm font-semibold ${
              showPublished
                ? "border-emerald-200 text-emerald-700"
                : "border-slate-200 text-slate-500"
            }`}
          >
            {showPublished ? "Published" : "No queued changes"}
          </div>
        )}
      </div>
    </div>
  );
}

function LaborPlanPublishConfirmModal({
  open,
  publishing,
  draftCount,
  invalidDraftCount,
  scenarioSummary,
  draftSummaries = [],
  forecastWindowLabel,
  onClose,
  onConfirm,
}) {
  const hasInvalidDrafts = invalidDraftCount > 0;
  const queuedDraftSubject =
    draftCount === 1 ? "Queued store draft" : "Queued store drafts";
  const impactLabel =
    scenarioSummary?.percentDelta === null ||
    scenarioSummary?.percentDelta === undefined
      ? `${queuedDraftSubject} impact is unavailable`
      : formatDraftVsCurrentTargetSummary(
          scenarioSummary.percentDelta,
          queuedDraftSubject,
          "current forecast"
        );

  return (
    <Modal
      open={Boolean(open)}
      onClose={() => {
        if (!publishing) {
          onClose?.();
        }
      }}
      aria-labelledby="labor-plan-publish-confirm-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxWidth: "92vw",
          bgcolor: "background.paper",
          border: "2px solid #E2E8F0",
          borderRadius: "15px",
          boxShadow: 24,
          p: 4,
          outline: "none",
        }}
      >
        <button
          type="button"
          className={`absolute right-3 top-3 text-gray-500 ${
            publishing ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={() => {
            if (!publishing) {
              onClose?.();
            }
          }}
          aria-label="Close publish confirmation"
        >
          <CloseIcon />
        </button>
        <div className="flex items-center gap-2">
          <span className="text-violet-700">
            <WarningIcon style={{ marginBottom: "2px" }} />
          </span>
          <h3
            id="labor-plan-publish-confirm-title"
            className="text-xl font-semibold text-slate-900"
          >
            Publish labor plan changes?
          </h3>
        </div>
        <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
          <p>
            This will save {draftCount} store-level {draftCount === 1 ? "change" : "changes"} and rerun forecasted labor for {forecastWindowLabel || "the visible forecast window"}.
          </p>
        </div>
        <div className="mt-5 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700">
          {impactLabel}
        </div>
        {draftSummaries.length ? (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-normal text-slate-500">
              Store drafts
            </p>
            <div className="mt-2 space-y-2 text-sm text-slate-600">
              {draftSummaries.map((draftSummary) => (
                <div
                  key={draftSummary.storeId}
                  className="grid grid-cols-[minmax(0,1fr)_max-content] items-start gap-4"
                >
                  <span className="min-w-0 truncate font-semibold text-slate-900">
                    {draftSummary.storeName}
                  </span>
                  <span className="whitespace-nowrap text-right">
                    {getDraftStoreSummaryText(draftSummary)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {hasInvalidDrafts ? (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Fix {invalidDraftCount} incomplete {invalidDraftCount === 1 ? "draft" : "drafts"} before publishing.
          </div>
        ) : null}
        <div className="mt-7 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
            onClick={onClose}
            disabled={publishing}
          >
            Cancel
          </button>
          <LoadingButton
            variant="contained"
            loading={publishing}
            loadingPosition="end"
            endIcon={<SaveIcon />}
            onClick={onConfirm}
            disabled={hasInvalidDrafts}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              paddingX: "18px",
              paddingY: "8px",
              backgroundColor: "#6d28d9",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
              "&.Mui-disabled": {
                backgroundColor: "#cbd5e1",
                color: "#64748b",
              },
              fontSize: "14px",
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            Publish and rerun forecast
          </LoadingButton>
        </div>
      </Box>
    </Modal>
  );
}

function getLaborPlanChartValue(row, metricKey) {
  if (!row) {
    return null;
  }
  if (metricKey === "forecasted") {
    return row.period === "forecast"
      ? getNumber(row?.planned?.hours) ?? getNumber(row?.forecasted?.hours)
      : null;
  }
  if (metricKey === "published") {
    return row.period === "forecast"
      ? getNumber(row?.planned?.hours) ?? getNumber(row?.forecasted?.hours)
      : getNumber(row?.earned?.hours);
  }
  if (metricKey === "scenario") {
    return getNumber(row?.[metricKey]?.hours);
  }
  return row.period === "historical" ? getNumber(row?.[metricKey]?.hours) : null;
}

function getLaborPlanChartSalesMetric(row) {
  if (!row) {
    return null;
  }

  const isForecast = row.period === "forecast";
  const sales = isForecast
    ? getNumber(row?.planned?.sales) ??
      getNumber(row?.forecasted?.sales) ??
      getNumber(row?.scenario?.sales)
    : getNumber(row?.actual?.sales) ?? getNumber(row?.earned?.sales);

  if (sales === null) {
    return null;
  }

  return {
    label: isForecast ? "Forecasted Sales" : "Actual Sales",
    value: formatCurrency(sales),
  };
}

function hasChartPointValue(point) {
  return point?.rawValue !== null && point?.rawValue !== undefined;
}

function clampValue(value, minValue, maxValue) {
  return Math.max(minValue, Math.min(maxValue, value));
}

function LaborPlanChartHeader({
  scenarioActive,
  scenarioLabel,
  showForecasted = true,
  forecastedLabel = LABOR_PLAN_CHART_CONFIG.forecastedLabel,
  legendBelow = false,
  interactiveLegend = false,
  consolidatePublishedSeries = false,
  visibleSeries = {},
  onToggleSeries,
  dense = false,
  description =
    "Historical actual and earned labor combined with the forecasted plan.",
}) {
  const legendItems = [
    {
      key: "actual",
      label: LABOR_PLAN_CHART_CONFIG.actualLabel,
      lineClassName: "bg-violet-700",
    },
    ...(consolidatePublishedSeries
      ? [{
          key: "published",
          label: "Published labor",
          lineClassName: "border-t-2 border-dashed border-violet-500",
        }]
      : [{
          key: "earned",
          label: LABOR_PLAN_CHART_CONFIG.earnedLabel,
          lineClassName: "border-t-2 border-dashed border-violet-500",
        }]),
    ...(!consolidatePublishedSeries && showForecasted
      ? [
          {
            key: "forecasted",
            label: forecastedLabel,
            lineClassName: "border-t-2 border-dotted border-violet-700",
          },
        ]
      : []),
    ...(scenarioActive
      ? [
          {
            key: "scenario",
            label: scenarioLabel,
            lineClassName: "border-t-2 border-dashed border-orange-600",
          },
        ]
      : []),
  ];

  return (
    <div
      className={
        legendBelow
          ? dense
            ? "space-y-1"
            : "space-y-2"
          : "flex items-center justify-between gap-4"
      }
    >
      <div>
        <h2
          className={`font-semibold text-slate-900 ${
            dense ? "text-lg" : "text-xl"
          }`}
        >
          {LABOR_PLAN_CHART_CONFIG.chartTitle}
        </h2>
        <p className={`${dense ? "mt-0.5 text-xs" : "mt-1 text-sm"} text-slate-500`}>
          {description}
        </p>
      </div>
      <div
        className={`flex flex-wrap items-center gap-y-1 text-slate-500 ${
          dense ? "gap-x-4 text-xs" : "gap-x-5 text-sm"
        }`}
      >
        {legendItems.map((item) => {
          const isVisible = visibleSeries[item.key] !== false;
          const content = (
            <>
              <span className={`h-0.5 w-7 ${item.lineClassName}`} />
              {item.label}
            </>
          );

          return interactiveLegend ? (
            <button
              key={item.key}
              type="button"
              aria-pressed={isVisible}
              aria-label={`Toggle ${item.label} line`}
              title={`Toggle ${item.label} line`}
              onClick={() => onToggleSeries?.(item.key)}
              className={`flex items-center gap-2 rounded px-1 py-0.5 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${
                isVisible ? "" : "opacity-40"
              }`}
            >
              {content}
            </button>
          ) : (
            <div key={item.key} className="flex items-center gap-2">
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LaborPlanComparisonChart({
  seriesRows,
  scenarioSeriesRows,
  storeCount,
  scenarioLabel = LABOR_PLAN_CHART_CONFIG.scenarioLabel,
  showForecasted = true,
  forecastedLabel = LABOR_PLAN_CHART_CONFIG.forecastedLabel,
  description,
  versionMarkers = [],
  legendBelow = false,
  compact = false,
  dense = false,
  interactiveLegend = false,
  consolidatePublishedSeries = false,
  visibleSeries: controlledVisibleSeries,
  onToggleSeries: controlledOnToggleSeries,
}) {
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [localVisibleSeries, setLocalVisibleSeries] = React.useState({
    actual: true,
    earned: true,
    forecasted: true,
    published: true,
    scenario: true,
  });
  const visibleSeries = controlledVisibleSeries || localVisibleSeries;
  const chartContainerRef = React.useRef(null);
  const [chartWidth, setChartWidth] = React.useState(920);
  const hasScenarioSeries =
    Array.isArray(scenarioSeriesRows) &&
    scenarioSeriesRows.length === seriesRows?.length &&
    scenarioSeriesRows.some((row) => getNumber(row?.scenario?.hours) !== null);
  const chartRows = hasScenarioSeries
    ? scenarioSeriesRows
    : Array.isArray(seriesRows)
      ? seriesRows
      : [];
  const isSeriesVisible = (seriesKey) => visibleSeries[seriesKey] !== false;
  const showPublishedSeries = consolidatePublishedSeries && hasScenarioSeries;
  const showEarnedSeries = !showPublishedSeries;
  const showForecastedSeries = showForecasted && !showPublishedSeries;
  const toggleSeries = (seriesKey) => {
    if (controlledOnToggleSeries) {
      controlledOnToggleSeries(seriesKey);
      return;
    }
    setLocalVisibleSeries((current) => ({
      ...current,
      [seriesKey]: current[seriesKey] === false,
    }));
  };
  const width = chartWidth;
  const height = dense ? 155 : compact ? 190 : 230;
  const padding = dense
    ? { top: 10, right: 18, bottom: 26, left: 42 }
    : compact
    ? { top: 14, right: 18, bottom: 30, left: 46 }
    : { top: 18, right: 18, bottom: 34, left: 46 };
  const usableWidth = width - padding.left - padding.right;

  React.useLayoutEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) {
      return undefined;
    }

    let resizeFrame = null;
    let settleFrame = null;
    let settleTimer = null;

    function syncChartWidth() {
      const measuredWidth = chartContainer.getBoundingClientRect().width;
      if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) {
        return;
      }

      const nextWidth = Math.max(320, Math.round(measuredWidth));
      setChartWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    }

    function scheduleChartWidthSync() {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }

      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        syncChartWidth();
      });
    }

    function cancelScheduledChartWidthSync() {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      if (settleFrame !== null) {
        window.cancelAnimationFrame(settleFrame);
      }
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
      }
    }

    syncChartWidth();
    settleFrame = window.requestAnimationFrame(() => {
      syncChartWidth();
      settleFrame = window.requestAnimationFrame(syncChartWidth);
    });
    settleTimer = window.setTimeout(syncChartWidth, 250);

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", scheduleChartWidthSync);
      return () => {
        window.removeEventListener("resize", scheduleChartWidthSync);
        cancelScheduledChartWidthSync();
      };
    }

    const resizeObserver = new ResizeObserver(scheduleChartWidthSync);
    resizeObserver.observe(chartContainer);
    window.addEventListener("resize", scheduleChartWidthSync);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleChartWidthSync);
      cancelScheduledChartWidthSync();
    };
  }, [chartRows.length, hasScenarioSeries, storeCount]);

  const maxHours = Math.max(
    ...chartRows.flatMap((row) => {
      return [
        getLaborPlanChartValue(row, "actual") || 0,
        getLaborPlanChartValue(row, "earned") || 0,
        getLaborPlanChartValue(row, "forecasted") || 0,
        getLaborPlanChartValue(row, "published") || 0,
        getLaborPlanChartValue(row, "scenario") || 0,
      ];
    }),
    1
  );
  const hasData = chartRows.some((row) => {
    return (
      (getLaborPlanChartValue(row, "actual") || 0) > 0 ||
      (getLaborPlanChartValue(row, "earned") || 0) > 0 ||
      (getLaborPlanChartValue(row, "forecasted") || 0) > 0
    );
  });
  const actualPoints = getChartPoints(
    chartRows,
    (row) => getLaborPlanChartValue(row, "actual"),
    width,
    height,
    padding,
    maxHours
  );
  const earnedPoints = getChartPoints(
    chartRows,
    (row) => getLaborPlanChartValue(row, "earned"),
    width,
    height,
    padding,
    maxHours
  );
  const forecastedPoints = showForecasted
    ? getChartPoints(
        chartRows,
        (row) => getLaborPlanChartValue(row, "forecasted"),
        width,
        height,
        padding,
        maxHours
      )
    : [];
  const publishedPoints = showPublishedSeries
    ? getChartPoints(
        chartRows,
        (row) => getLaborPlanChartValue(row, "published"),
        width,
        height,
        padding,
        maxHours
      )
    : [];
  const scenarioPoints = hasScenarioSeries
    ? getChartPoints(
        chartRows,
        (row) => getLaborPlanChartValue(row, "scenario"),
        width,
        height,
        padding,
        maxHours
      )
    : [];
  const actualPath = getChartPathFromPoints(actualPoints);
  const earnedPath = getChartPathFromPoints(earnedPoints);
  const forecastedPath = getChartPathFromPoints(forecastedPoints);
  const publishedPath = getChartPathFromPoints(publishedPoints);
  const scenarioPath = getChartPathFromPoints(scenarioPoints);
  const forecastStartIndex = chartRows.findIndex(
    (row) => row?.period === "forecast"
  );
  const forecastStartX =
    forecastStartIndex >= 0
      ? padding.left +
        (forecastStartIndex / Math.max(chartRows.length - 1, 1)) * usableWidth
      : null;
  const activeActualPoint =
    activeIndex === null ? null : actualPoints[activeIndex] || null;
  const activeEarnedPoint =
    activeIndex === null ? null : earnedPoints[activeIndex] || null;
  const activeForecastedPoint =
    activeIndex === null ? null : forecastedPoints[activeIndex] || null;
  const activePublishedPoint =
    activeIndex === null ? null : publishedPoints[activeIndex] || null;
  const activeScenarioPoint =
    activeIndex === null ? null : scenarioPoints[activeIndex] || null;
  const activeAnchorPoint = [
    isSeriesVisible("actual") ? activeActualPoint : null,
    showEarnedSeries && isSeriesVisible("earned") ? activeEarnedPoint : null,
    showForecastedSeries && isSeriesVisible("forecasted") ? activeForecastedPoint : null,
    showPublishedSeries && isSeriesVisible("published") ? activePublishedPoint : null,
    isSeriesVisible("scenario") ? activeScenarioPoint : null,
  ].find(hasChartPointValue) || null;
  const activeRow =
    activeIndex === null ? null : chartRows[activeIndex] || null;
  const activeHistoricalVariance = showPublishedSeries
    ? isSeriesVisible("scenario") &&
      isSeriesVisible("actual") &&
      hasChartPointValue(activeScenarioPoint) &&
      hasChartPointValue(activeActualPoint)
        ? activeScenarioPoint.rawValue - activeActualPoint.rawValue
        : null
    : isSeriesVisible("actual") &&
        isSeriesVisible("earned") &&
        hasChartPointValue(activeActualPoint) &&
        hasChartPointValue(activeEarnedPoint)
      ? activeActualPoint.rawValue - activeEarnedPoint.rawValue
      : null;
  const activeScenarioComparisonPoint =
    showPublishedSeries
      ? activePublishedPoint
      : activeRow?.period === "historical"
        ? activeEarnedPoint
        : activeForecastedPoint;
  const activeScenarioVariance =
    isSeriesVisible("scenario") &&
    (showPublishedSeries
      ? isSeriesVisible("published")
      : isSeriesVisible(
          activeRow?.period === "historical" ? "earned" : "forecasted"
        )) &&
    hasChartPointValue(activeScenarioPoint) &&
    hasChartPointValue(activeScenarioComparisonPoint)
      ? activeScenarioPoint.rawValue - activeScenarioComparisonPoint.rawValue
      : null;
  const activeSalesMetric = getLaborPlanChartSalesMetric(activeRow);
  const activeValuePoints = [
    isSeriesVisible("actual") ? activeActualPoint : null,
    showEarnedSeries && isSeriesVisible("earned") ? activeEarnedPoint : null,
    showForecastedSeries && isSeriesVisible("forecasted") ? activeForecastedPoint : null,
    showPublishedSeries && isSeriesVisible("published") ? activePublishedPoint : null,
    isSeriesVisible("scenario") ? activeScenarioPoint : null,
  ].filter(hasChartPointValue);
  const activeAnchorY =
    activeValuePoints.length > 0
      ? activeValuePoints.reduce((total, point) => total + point.y, 0) /
        activeValuePoints.length
      : height / 2;
  const tooltipTop = clampValue(
    activeAnchorY - LABOR_PLAN_TOOLTIP_HEIGHT_ESTIMATE / 2,
    8,
    Math.max(8, height - LABOR_PLAN_TOOLTIP_HEIGHT_ESTIMATE - 8)
  );
  const hasTooltipRoomRight =
    activeAnchorPoint &&
    width - activeAnchorPoint.x >=
      LABOR_PLAN_TOOLTIP_WIDTH + LABOR_PLAN_TOOLTIP_GAP + padding.right;
  const hasTooltipRoomLeft =
    activeAnchorPoint &&
    activeAnchorPoint.x >=
      LABOR_PLAN_TOOLTIP_WIDTH + LABOR_PLAN_TOOLTIP_GAP + padding.left;
  const tooltipOnRight =
    hasTooltipRoomRight &&
    (!hasTooltipRoomLeft || activeAnchorPoint.x < width / 2);
  const tooltipOnLeft = hasTooltipRoomLeft && !tooltipOnRight;
  const activeAnchorPercent = activeAnchorPoint
    ? (activeAnchorPoint.x / Math.max(width, 1)) * 100
    : 0;
  const tooltipCenterMinPercent =
    ((LABOR_PLAN_TOOLTIP_WIDTH / 2 + padding.left) / Math.max(width, 1)) * 100;
  const tooltipCenterMaxPercent =
    100 -
    ((LABOR_PLAN_TOOLTIP_WIDTH / 2 + padding.right) / Math.max(width, 1)) * 100;
  const tooltipLeft = activeAnchorPoint
    ? tooltipOnRight
      ? `calc(${activeAnchorPercent}% + ${LABOR_PLAN_TOOLTIP_GAP}px)`
      : tooltipOnLeft
        ? `calc(${activeAnchorPercent}% - ${LABOR_PLAN_TOOLTIP_GAP}px)`
        : `${clampValue(
            activeAnchorPercent,
            tooltipCenterMinPercent,
            Math.max(tooltipCenterMinPercent, tooltipCenterMaxPercent)
          )}%`
    : "0%";
  const tooltipTransform = tooltipOnRight
    ? "none"
    : tooltipOnLeft
      ? "translateX(-100%)"
      : "translate(-50%, calc(-100% - 12px))";
  const gridValues = [0, maxHours / 2, maxHours];
  const xAxisLabels = [0, Math.floor((chartRows.length - 1) / 2), chartRows.length - 1]
    .filter((index, position, indexes) => index >= 0 && indexes.indexOf(index) === position)
    .map((index) => ({ index, row: chartRows[index] }));
  const versionMarkerPoints = versionMarkers
    .map((marker) => {
      const index = chartRows.findIndex((row) => row?.date === marker.date);
      if (index < 0) return null;
      return {
        ...marker,
        index,
        x:
          padding.left +
          (index / Math.max(chartRows.length - 1, 1)) * usableWidth,
      };
    })
    .filter(Boolean);
  function handleChartMouseMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const rawIndex = Math.round(
      ((x - padding.left) / usableWidth) * (chartRows.length - 1)
    );
    const nextIndex = Math.max(0, Math.min(chartRows.length - 1, rawIndex));
    setActiveIndex(nextIndex);
  }

  if (chartRows.length < 2 || !hasData) {
    return (
      <div
        className={
          dense
            ? "mt-3 border-t border-slate-200 pt-3"
            : compact
            ? "mt-4 border-t border-slate-200 pt-4"
            : "mt-6 border-t border-slate-200 pt-6"
        }
      >
        <LaborPlanChartHeader
          scenarioActive={hasScenarioSeries}
          scenarioLabel={scenarioLabel}
          showForecasted={showForecasted}
          forecastedLabel={forecastedLabel}
          description={description}
          legendBelow={legendBelow}
          interactiveLegend={interactiveLegend}
          consolidatePublishedSeries={showPublishedSeries}
          visibleSeries={visibleSeries}
          onToggleSeries={toggleSeries}
          dense={dense}
        />
        <div
          className={`rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 text-sm text-slate-500 ${
            dense ? "mt-2 py-5" : compact ? "mt-3 py-6" : "mt-4 py-8"
          }`}
        >
          Time series will appear after the selected timeframe has{" "}
          {LABOR_PLAN_CHART_CONFIG.emptyLabel} data.
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        dense
          ? "mt-3 border-t border-slate-200 pt-3"
          : compact
          ? "mt-4 border-t border-slate-200 pt-4"
          : "mt-6 border-t border-slate-200 pt-6"
      }
    >
      <LaborPlanChartHeader
        scenarioActive={hasScenarioSeries}
        scenarioLabel={scenarioLabel}
        showForecasted={showForecasted}
        forecastedLabel={forecastedLabel}
        description={description}
        legendBelow={legendBelow}
        interactiveLegend={interactiveLegend}
        consolidatePublishedSeries={showPublishedSeries}
        visibleSeries={visibleSeries}
        onToggleSeries={toggleSeries}
        dense={dense}
      />
      <div
        ref={chartContainerRef}
        className={`relative w-full ${
          dense
            ? "mt-2 h-[155px]"
            : compact
            ? "mt-3 h-[190px]"
            : "mt-4 h-[230px]"
        }`}
      >
        <svg
          className="h-full w-full overflow-visible"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={LABOR_PLAN_CHART_CONFIG.chartTitle}
          onMouseMove={handleChartMouseMove}
          onMouseLeave={() => setActiveIndex(null)}
        >
          <defs>
            <linearGradient id="labor-plan-fill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.16" />
              <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {gridValues.map((value) => {
            const y =
              padding.top +
              (1 - value / maxHours) *
                (height - padding.top - padding.bottom);
            return (
              <g key={value}>
                <line
                  x1={padding.left}
                  x2={width - padding.right}
                  y1={y}
                  y2={y}
                  stroke="#e2e8f0"
                  strokeDasharray="4 4"
                />
                <text x={0} y={y + 4} fill="#64748b" fontSize="12">
                  {formatNumber(value, 0)}
                </text>
              </g>
            );
          })}
          {forecastStartX !== null ? (
            <g>
              <line
                x1={forecastStartX}
                x2={forecastStartX}
                y1={padding.top}
                y2={height - padding.bottom}
                stroke="#94a3b8"
                strokeDasharray="5 5"
                strokeWidth="1.5"
              />
              <text
                x={forecastStartX + 8}
                y={padding.top + 12}
                fill="#64748b"
                fontSize="12"
                fontWeight="600"
              >
                Forecasted starts
              </text>
            </g>
          ) : null}
          {versionMarkerPoints.map((marker) => (
            <g key={`${marker.date}-${marker.label}`}>
              <title>{marker.description || marker.label}</title>
              <line
                x1={marker.x}
                x2={marker.x}
                y1={height - padding.bottom - 3}
                y2={height - padding.bottom + 5}
                stroke="#7c3aed"
                strokeWidth="2"
              />
              <text
                x={marker.x}
                y={height - padding.bottom + 18}
                fill="#6d28d9"
                fontSize="11"
                fontWeight="600"
                textAnchor="middle"
              >
                {marker.label}
              </text>
            </g>
          ))}
          {isSeriesVisible("actual") ? (
            <path
              d={actualPath}
              fill="none"
              stroke="#4f46e5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          ) : null}
          {showEarnedSeries && isSeriesVisible("earned") ? (
            <path
              d={earnedPath}
              fill="none"
              stroke="#8b5cf6"
              strokeDasharray="4 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          ) : null}
          {showForecastedSeries && isSeriesVisible("forecasted") ? (
            <path
              d={forecastedPath}
              fill="none"
              stroke="#6d28d9"
              strokeDasharray="2 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          ) : null}
          {showPublishedSeries && isSeriesVisible("published") ? (
            <path
              d={publishedPath}
              fill="none"
              stroke="#8b5cf6"
              strokeDasharray="4 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          ) : null}
          {hasScenarioSeries && isSeriesVisible("scenario") ? (
            <path
              d={scenarioPath}
              fill="none"
              stroke="#ea580c"
              strokeDasharray="7 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          ) : null}
          {activeAnchorPoint ? (
            <line
              x1={activeAnchorPoint.x}
              x2={activeAnchorPoint.x}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#94a3b8"
              strokeDasharray="3 4"
              strokeWidth="1"
            />
          ) : null}
          {isSeriesVisible("actual") && hasChartPointValue(activeActualPoint) ? (
            <circle
              cx={activeActualPoint.x}
              cy={activeActualPoint.y}
              r="4"
              fill="#4f46e5"
              stroke="#ffffff"
              strokeWidth="2"
            />
          ) : null}
          {showEarnedSeries && isSeriesVisible("earned") && hasChartPointValue(activeEarnedPoint) ? (
            <circle
              cx={activeEarnedPoint.x}
              cy={activeEarnedPoint.y}
              r="4"
              fill="#ffffff"
              stroke="#8b5cf6"
              strokeWidth="2"
            />
          ) : null}
          {showForecastedSeries && isSeriesVisible("forecasted") && hasChartPointValue(activeForecastedPoint) ? (
            <circle
              cx={activeForecastedPoint.x}
              cy={activeForecastedPoint.y}
              r="4"
              fill="#ffffff"
              stroke="#6d28d9"
              strokeWidth="2"
            />
          ) : null}
          {showPublishedSeries && isSeriesVisible("published") && hasChartPointValue(activePublishedPoint) ? (
            <circle
              cx={activePublishedPoint.x}
              cy={activePublishedPoint.y}
              r="4"
              fill="#ffffff"
              stroke="#8b5cf6"
              strokeWidth="2"
            />
          ) : null}
          {isSeriesVisible("scenario") && hasChartPointValue(activeScenarioPoint) ? (
            <circle
              cx={activeScenarioPoint.x}
              cy={activeScenarioPoint.y}
              r="4"
              fill="#ffffff"
              stroke="#ea580c"
              strokeWidth="2"
            />
          ) : null}
          {xAxisLabels.map(({ index, row }) => {
            const x =
              padding.left +
              (index / Math.max(chartRows.length - 1, 1)) *
                (width - padding.left - padding.right);
            return (
              <text
                key={`${row?.date}-${index}`}
                x={x}
                y={height - 5}
                fill="#64748b"
                fontSize="12"
                textAnchor={
                  index === 0
                    ? "start"
                    : index === chartRows.length - 1
                      ? "end"
                      : "middle"
                }
              >
                {formatShortDate(row?.date)}
              </text>
            );
          })}
          <rect
            x={padding.left}
            y={padding.top}
            width={usableWidth}
            height={height - padding.top - padding.bottom}
            fill="transparent"
            pointerEvents="all"
          />
        </svg>

        {activeAnchorPoint && activeRow ? (
          <div
            className="pointer-events-none absolute z-20 w-[280px] rounded-lg border border-slate-200 bg-white p-3 text-xs opacity-100 shadow-xl"
            style={{
              left: tooltipLeft,
              top: tooltipOnRight || tooltipOnLeft ? `${tooltipTop}px` : "0px",
              transform: tooltipTransform,
            }}
          >
            <div className="flex items-center justify-between gap-4 font-semibold text-slate-700">
              <p>{formatShortDate(activeRow?.date)}</p>
              {storeCount !== undefined ? (
                <p className="font-medium text-slate-500">
                  {formatStoreCount(storeCount)}
                </p>
              ) : null}
            </div>
            <div className="mt-2 space-y-1.5">
              {activeSalesMetric ? (
                <div className="flex items-center justify-between gap-4">
                  <p className="text-slate-500">
                    {activeSalesMetric.label}
                  </p>
                  <p className="shrink-0 text-right font-semibold text-slate-900">
                    {activeSalesMetric.value}
                  </p>
                </div>
              ) : null}
            </div>
            <div className="mt-2 space-y-1.5 border-t border-slate-200 pt-2">
              {isSeriesVisible("actual") && hasChartPointValue(activeActualPoint) ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <span className="h-0.5 w-4 shrink-0 bg-violet-700" />
                    Actual used labor
                  </div>
                  <p className="shrink-0 text-right font-semibold text-slate-900">
                    {formatNumber(activeActualPoint.value, 1)} hrs
                  </p>
                </div>
              ) : null}
              {showEarnedSeries && isSeriesVisible("earned") && hasChartPointValue(activeEarnedPoint) ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <span className="h-0.5 w-4 shrink-0 border-t-2 border-dashed border-violet-500" />
                    Published earned
                  </div>
                  <p className="shrink-0 text-right font-semibold text-slate-900">
                    {formatNumber(activeEarnedPoint.value, 1)} hrs
                  </p>
                </div>
              ) : null}
              {showForecastedSeries && isSeriesVisible("forecasted") && hasChartPointValue(activeForecastedPoint) ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <span className="h-0.5 w-4 shrink-0 border-t-2 border-dotted border-violet-700" />
                    Published plan
                  </div>
                  <p className="shrink-0 text-right font-semibold text-slate-900">
                    {formatNumber(activeForecastedPoint.value, 1)} hrs
                  </p>
                </div>
              ) : null}
              {showPublishedSeries && isSeriesVisible("published") && hasChartPointValue(activePublishedPoint) ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <span className="h-0.5 w-4 shrink-0 border-t-2 border-dashed border-violet-500" />
                    Published labor
                  </div>
                  <p className="shrink-0 text-right font-semibold text-slate-900">
                    {formatNumber(activePublishedPoint.value, 1)} hrs
                  </p>
                </div>
              ) : null}
              {isSeriesVisible("scenario") && hasChartPointValue(activeScenarioPoint) ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-slate-500">
                    <span className="h-0.5 w-4 shrink-0 border-t-2 border-dashed border-orange-600" />
                    {showPublishedSeries ? "Sandbox labor" : "Selected model"}
                  </div>
                  <p className="shrink-0 text-right font-semibold text-slate-900">
                    {formatNumber(activeScenarioPoint.value, 1)} hrs
                  </p>
                </div>
              ) : null}
            </div>
            {activeHistoricalVariance !== null || activeScenarioVariance !== null ? (
              <div className="mt-2 border-t border-slate-200 pt-2">
                {activeHistoricalVariance !== null ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-slate-500">
                      {showPublishedSeries ? "Sandbox vs actual used" : "Actual vs earned"}
                    </p>
                    <p className="shrink-0 text-right font-semibold text-slate-900">
                      {formatDeltaHours(activeHistoricalVariance)}
                    </p>
                  </div>
                ) : null}
                {activeScenarioVariance !== null ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-slate-500">
                      {showPublishedSeries
                        ? "Sandbox vs published"
                        : activeRow?.period === "historical"
                          ? "Selected vs earned"
                          : "Selected vs forecast"}
                    </p>
                    <p className="shrink-0 text-right font-semibold text-slate-900">
                      {formatDeltaHours(activeScenarioVariance)}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LocationDetailsLaborPlanTable(props) {
  const storesInfo = React.useMemo(
    () => (Array.isArray(props.storesInfo) ? props.storesInfo : []),
    [props.storesInfo]
  );
  const laborPlanSummary = props.laborPlanSummary || {};
  const [dynamicBaselineRowsByStoreId, setDynamicBaselineRowsByStoreId] =
    React.useState({});
  const [dynamicBaselineLoadingByStoreId, setDynamicBaselineLoadingByStoreId] =
    React.useState({});
  const [dynamicBaselineErrorByStoreId, setDynamicBaselineErrorByStoreId] =
    React.useState({});
  const dynamicBaselineRequestRef = React.useRef({});
  const dynamicBaselineRequestSequenceRef = React.useRef(0);
  const planRows = React.useMemo(
    () => (Array.isArray(laborPlanSummary.stores) ? laborPlanSummary.stores : []),
    [laborPlanSummary.stores]
  );
  const seriesRows = React.useMemo(
    () => (Array.isArray(laborPlanSummary.series) ? laborPlanSummary.series : []),
    [laborPlanSummary.series]
  );
  const storeSeriesByStoreId = React.useMemo(
    () => laborPlanSummary.store_series || {},
    [laborPlanSummary.store_series]
  );
  const planRowByStoreId = React.useMemo(() => {
    const nextMap = new Map();
    planRows.forEach((row) => {
      const dynamicBaselineRow =
        dynamicBaselineRowsByStoreId[String(row.store_id)];
      const mergedRow = dynamicBaselineRow?.bottom_up_forecasted
        ? {
            ...row,
            bottom_up_forecasted: dynamicBaselineRow.bottom_up_forecasted,
          }
        : row;
      nextMap.set(row.store_id, mergedRow);
    });
    return nextMap;
  }, [dynamicBaselineRowsByStoreId, planRows]);
  const storePlans = React.useMemo(
    () =>
      storesInfo.map((store) =>
        getStorePlan(store, planRowByStoreId.get(store.id))
      ),
    [storesInfo, planRowByStoreId]
  );
  const storePlanByStoreId = React.useMemo(() => {
    const nextMap = new Map();
    storePlans.forEach((row) => {
      if (row?.store?.id) {
        nextMap.set(String(row.store.id), row);
      }
    });
    return nextMap;
  }, [storePlans]);
  const allCompanyStoreIds = React.useMemo(
    () => storesInfo.map((store) => String(store.id)),
    [storesInfo]
  );

  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [modalData, setModalData] = React.useState(null);
  const [modalPlanRow, setModalPlanRow] = React.useState(null);
  const [selectedTargetChoice, setSelectedTargetChoice] =
    React.useState(TARGET_DYNAMIC);
  const [targetValue, setTargetValue] = React.useState("");
  const [adjustmentValue, setAdjustmentValue] = React.useState("0");
  const [draftChangesByStoreId, setDraftChangesByStoreId] = React.useState({});
  const [companyPlanStoreIds, setCompanyPlanStoreIds] = React.useState([]);
  const [publishedState, setPublishedState] = React.useState(null);
  const publishedStateTimerRef = React.useRef(null);
  const [publishing, setPublishing] = React.useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = React.useState(false);
  const comparisonConfig = TABLE_COMPARISON_CONFIG;
  const selectedCompanyStoreIds = React.useMemo(() => {
    if (companyPlanStoreIds.length === 0) {
      return allCompanyStoreIds;
    }

    const allCompanyStoreIdSet = new Set(allCompanyStoreIds);
    const validStoreIds = companyPlanStoreIds
      .map((storeId) => String(storeId))
      .filter((storeId) => allCompanyStoreIdSet.has(storeId));

    return validStoreIds.length > 0 ? validStoreIds : allCompanyStoreIds;
  }, [allCompanyStoreIds, companyPlanStoreIds]);
  const selectedCompanyStoreIdSet = React.useMemo(
    () => new Set(selectedCompanyStoreIds),
    [selectedCompanyStoreIds]
  );
  const companyStoreFilterActive =
    selectedCompanyStoreIds.length !== allCompanyStoreIds.length;
  const windowLabels = React.useMemo(
    () => getLaborPlanWindowLabels(laborPlanSummary.series_window || {}),
    [laborPlanSummary.series_window]
  );
  const windowDayCounts = React.useMemo(
    () => getLaborPlanWindowDayCounts(laborPlanSummary.series_window || {}),
    [laborPlanSummary.series_window]
  );
  const dynamicBaselineCacheKey = [
    props.company_id || "",
    laborPlanSummary.window?.requested_start_date ||
      laborPlanSummary.window?.start_date ||
      "",
    laborPlanSummary.window?.requested_end_date ||
      laborPlanSummary.window?.end_date ||
      "",
    laborPlanSummary.series_window?.forecast_start_date || "",
    laborPlanSummary.series_window?.forecast_end_date || "",
  ].join("|");
  const selectedStoreCount = selectedCompanyStoreIds.length;

  const baseAggregate = React.useMemo(
    () => laborPlanSummary.aggregate || {},
    [laborPlanSummary.aggregate]
  );
  const laborPercentEnabled = storesInfo.some((store) =>
    Boolean(store?.labor_percent_enabled)
  );
  const selectedCompanyPlanRows = React.useMemo(() => {
    return planRows.filter((row) =>
      selectedCompanyStoreIdSet.has(String(row.store_id))
    );
  }, [planRows, selectedCompanyStoreIdSet]);
  const aggregate = React.useMemo(() => {
    return companyStoreFilterActive
      ? buildAggregateFromPlanRows(selectedCompanyPlanRows)
      : baseAggregate;
  }, [baseAggregate, companyStoreFilterActive, selectedCompanyPlanRows]);
  const companySeriesRows = React.useMemo(() => {
    return companyStoreFilterActive
      ? buildCompanySeriesRowsFromStores(
          seriesRows,
          storeSeriesByStoreId,
          selectedCompanyStoreIds
        )
      : seriesRows;
  }, [
    companyStoreFilterActive,
    selectedCompanyStoreIds,
    seriesRows,
    storeSeriesByStoreId,
  ]);
  const draftRows = React.useMemo(() => {
    return Object.entries(draftChangesByStoreId)
      .map(([storeId, draft]) => {
        const row = storePlanByStoreId.get(String(storeId));
        if (!row) {
          return null;
        }
        const baselineLoading = Boolean(
          dynamicBaselineLoadingByStoreId[String(storeId)]
        );
        const baselineError = Boolean(
          dynamicBaselineErrorByStoreId[String(storeId)]
        );
        const baselinePending =
          needsDynamicBaseline(row, draft.targetChoice) && !baselineError;
        const validationMessage =
          getLaborPlanDraftValidationMessage(
            draft.targetChoice,
            draft.targetValue,
            draft.adjustmentValue,
            canUseFixedLaborPercentTarget(row.store, row.planRow)
          ) ||
          getDynamicBaselineValidationMessage(
            row,
            draft.targetChoice,
            baselineLoading,
            baselineError
          );
        const scenario = validationMessage
          ? null
          : getLaborPlanScenario(
              row,
              comparisonConfig,
              draft.targetChoice,
              draft.targetValue,
              draft.adjustmentValue
            );

        return {
          row,
          draft,
          baselineLoading: baselinePending,
          validationMessage,
          scenario,
        };
      })
      .filter(Boolean);
  }, [
    comparisonConfig,
    draftChangesByStoreId,
    dynamicBaselineErrorByStoreId,
    dynamicBaselineLoadingByStoreId,
    storePlanByStoreId,
  ]);
  const draftScenarios = React.useMemo(
    () => draftRows.map((row) => row.scenario).filter(Boolean),
    [draftRows]
  );
  const draftStoreSummaries = React.useMemo(() => {
    return draftRows.map((draftRow) => ({
      storeId: String(draftRow.row?.store?.id || ""),
      storeName: draftRow.row?.store?.store_name || "Store",
      percentDelta: getNumber(draftRow.scenario?.preview?.percentDelta),
      validationMessage: draftRow.validationMessage,
    }));
  }, [draftRows]);
  const companyDraftScenarios = React.useMemo(() => {
    return draftScenarios.filter((scenario) =>
      selectedCompanyStoreIdSet.has(String(scenario.storeId))
    );
  }, [draftScenarios, selectedCompanyStoreIdSet]);
  const draftRowByStoreId = React.useMemo(() => {
    const nextMap = new Map();
    draftRows.forEach((draftRow) => {
      nextMap.set(String(draftRow.row.store.id), draftRow);
    });
    return nextMap;
  }, [draftRows]);
  const filteredStorePlans = React.useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return storePlans
      .filter((row) => {
        return (
          selectedCompanyStoreIdSet.has(String(row.store?.id)) &&
          (!normalizedSearch ||
            row.store?.store_name?.toLowerCase().includes(normalizedSearch))
        );
      })
      .sort(compareStorePlansByGap);
  }, [searchTerm, selectedCompanyStoreIdSet, storePlans]);
  const invalidDraftCount = draftRows.filter(
    (row) => row.validationMessage
  ).length;
  const draftScenarioSummary = React.useMemo(() => {
    const deltaHours = draftScenarios.reduce(
      (total, scenario) =>
        total +
        (getNumber(scenario.preview?.deltaHours) ??
          getNumber(scenario.deltaHours) ??
          0),
      0
    );
    const currentTargetHours = draftScenarios.reduce((total, scenario) => {
      const scenarioHours = getNumber(scenario.metrics?.comparisonHours);
      const scenarioDeltaHours =
        getNumber(scenario.preview?.deltaHours) ?? getNumber(scenario.deltaHours);
      if (scenarioHours === null || scenarioDeltaHours === null) {
        return total;
      }
      return total + (scenarioHours - scenarioDeltaHours);
    }, 0);
    const costDelta = draftScenarios.reduce(
      (total, scenario) => total + (getNumber(scenario.preview?.costDelta) || 0),
      0
    );
    const percentDelta =
      currentTargetHours > 0 && deltaHours !== null
        ? (deltaHours / currentTargetHours) * 100
        : null;

    return {
      deltaHours,
      costDelta,
      percentDelta,
    };
  }, [draftScenarios]);
  const activeStorePlan = modalData?.id
    ? storePlanByStoreId.get(String(modalData.id)) || null
    : null;
  const modalStoreId = modalData?.id ? String(modalData.id) : null;
  const modalDynamicBaselineLoading = modalStoreId
    ? Boolean(dynamicBaselineLoadingByStoreId[modalStoreId])
    : false;
  const modalDynamicBaselineError = modalStoreId
    ? Boolean(dynamicBaselineErrorByStoreId[modalStoreId])
    : false;
  const modalDynamicBaselinePending =
    needsDynamicBaseline(activeStorePlan, selectedTargetChoice) &&
    !modalDynamicBaselineError;
  const modalPreview = getPreviewPlan(
    selectedTargetChoice,
    targetValue,
    adjustmentValue,
    modalPlanRow
  );
  const modalTargetMetric = applyFixedTargetDisplayValue(
    buildPlanPreviewMetric(
      modalPlanRow,
      modalPreview.hours,
      modalPreview.costDelta,
      getTargetAverageWage(modalPlanRow),
      modalPlanRow?.planned
    ),
    selectedTargetChoice,
    targetValue
  );
  const modalLaborPercentEnabled = Boolean(modalData?.labor_percent_enabled);
  const modalFixedLaborPercentAvailable = canUseFixedLaborPercentTarget(
    modalData,
    modalPlanRow
  );
  const modalTargetOptions = React.useMemo(
    () =>
      TARGET_OPTIONS.filter(
        (option) =>
          option.key !== TARGET_LABOR_PERCENT ||
          modalFixedLaborPercentAvailable ||
          selectedTargetChoice === TARGET_LABOR_PERCENT
      ),
    [modalFixedLaborPercentAvailable, selectedTargetChoice]
  );
  const modalDraftActive = Boolean(
    modalData?.id && draftChangesByStoreId[String(modalData.id)]
  );
  const modalDraftValidationMessage =
    getLaborPlanDraftValidationMessage(
      selectedTargetChoice,
      targetValue,
      adjustmentValue,
      modalFixedLaborPercentAvailable
    ) ||
    getDynamicBaselineValidationMessage(
      activeStorePlan,
      selectedTargetChoice,
      modalDynamicBaselinePending,
      modalDynamicBaselineError
    );
  const modalDraftStatus = modalDynamicBaselinePending
    ? {
        title: "Loading dynamic baseline",
        message:
          "Draft forecast will update after the labor model baseline finishes loading.",
        className: "border-violet-100 bg-violet-50/60 text-slate-600",
        titleClassName: "text-slate-900",
      }
    : modalDraftValidationMessage
    ? {
        title: "Draft needs attention",
        message: modalDraftValidationMessage,
        className: "border-orange-200 bg-orange-50 text-orange-700",
        titleClassName: "text-orange-800",
      }
    : modalDraftActive
      ? {
          title: "Queued draft",
          message:
            "This store has a queued draft. It will not apply until you publish changes. Use Reset store to remove this draft.",
          className: "border-orange-200 bg-orange-50 text-orange-700",
          titleClassName: "text-orange-800",
        }
      : {
          title: "Store draft workflow",
          message:
            "Edits in this drawer create a store draft. Drafts stay pending until you publish changes from the company overview.",
          className: "border-violet-100 bg-violet-50/60 text-slate-600",
          titleClassName: "text-slate-900",
        };
  const scenarioSeriesRows = React.useMemo(
    () =>
      getScenarioSeriesRows(
        companySeriesRows,
        comparisonConfig,
        companyDraftScenarios
      ),
    [companyDraftScenarios, companySeriesRows, comparisonConfig]
  );

  React.useEffect(() => {
    dynamicBaselineRequestRef.current = {};
    setDynamicBaselineRowsByStoreId({});
    setDynamicBaselineLoadingByStoreId({});
    setDynamicBaselineErrorByStoreId({});
  }, [dynamicBaselineCacheKey]);

  const loadDynamicBaselineForStore = React.useCallback(
    async (storeId) => {
      if (!props.company_id || !storeId) {
        return;
      }

      const storeKey = String(storeId);
      const requestId = dynamicBaselineRequestSequenceRef.current + 1;
      dynamicBaselineRequestSequenceRef.current = requestId;
      const requestCacheKey = dynamicBaselineCacheKey;
      dynamicBaselineRequestRef.current[storeKey] = {
        cacheKey: requestCacheKey,
        requestId,
      };
      const isCurrentDynamicBaselineRequest = () => {
        const activeRequest = dynamicBaselineRequestRef.current[storeKey];
        return (
          activeRequest?.requestId === requestId &&
          activeRequest?.cacheKey === requestCacheKey
        );
      };
      setDynamicBaselineLoadingByStoreId((current) => ({
        ...current,
        [storeKey]: true,
      }));
      setDynamicBaselineErrorByStoreId((current) => {
        const next = { ...current };
        delete next[storeKey];
        return next;
      });

      const windowParams = laborPlanSummary.window || {};
      const seriesWindowParams = laborPlanSummary.series_window || {};
      const requestParams = {
        company_id: props.company_id,
        store_ids: storeKey,
        start_date:
          windowParams.requested_start_date || windowParams.start_date || "",
        end_date: windowParams.requested_end_date || windowParams.end_date || "",
        forecast_start_date: seriesWindowParams.forecast_start_date || "",
        forecast_end_date: seriesWindowParams.forecast_end_date || "",
        exclude_anomalies: windowParams.exclude_anomalies ?? true,
        include_bottom_up_forecasted: true,
      };

      try {
        const response = await getRightworkAPI(
          API_get_labor_plan_summary,
          requestParams
        );
        if (!isCurrentDynamicBaselineRequest()) {
          return;
        }
        const laborPlan =
          response?.data?.redux?.payload?.labor_plan || {};
        const fetchedRow = Array.isArray(laborPlan.stores)
          ? laborPlan.stores[0]
          : null;
        if (
          !fetchedRow ||
          getNumber(fetchedRow?.bottom_up_forecasted?.hours) === null
        ) {
          throw new Error("Dynamic baseline was not returned");
        }
        setDynamicBaselineRowsByStoreId((current) => ({
          ...current,
          [storeKey]: fetchedRow,
        }));
      } catch (error) {
        if (!isCurrentDynamicBaselineRequest()) {
          return;
        }
        setDynamicBaselineErrorByStoreId((current) => ({
          ...current,
          [storeKey]: true,
        }));
        toast.error("Could not load dynamic labor baseline", {
          autoClose: 3000,
        });
      } finally {
        if (isCurrentDynamicBaselineRequest()) {
          setDynamicBaselineLoadingByStoreId((current) => {
            const next = { ...current };
            delete next[storeKey];
            return next;
          });
        }
      }
    },
    [
      dynamicBaselineCacheKey,
      laborPlanSummary.series_window,
      laborPlanSummary.window,
      props.company_id,
    ]
  );

  React.useEffect(() => {
    setCompanyPlanStoreIds((currentStoreIds) => {
      if (allCompanyStoreIds.length === 0) {
        return [];
      }

      if (currentStoreIds.length === 0) {
        return allCompanyStoreIds;
      }

      const allStoreIdSet = new Set(allCompanyStoreIds);
      const validStoreIds = currentStoreIds.filter((storeId) =>
        allStoreIdSet.has(String(storeId))
      );

      const nextStoreIds =
        validStoreIds.length > 0 ? validStoreIds : allCompanyStoreIds;
      if (
        nextStoreIds.length === currentStoreIds.length &&
        nextStoreIds.every(
          (storeId, index) => String(storeId) === String(currentStoreIds[index])
        )
      ) {
        return currentStoreIds;
      }

      return nextStoreIds;
    });
  }, [allCompanyStoreIds]);

  React.useEffect(() => {
    return () => {
      if (publishedStateTimerRef.current) {
        clearTimeout(publishedStateTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (draftRows.length > 0 && publishedState) {
      setPublishedState(null);
    }
  }, [draftRows.length, publishedState]);

  React.useEffect(() => {
    draftRows.forEach((draftRow) => {
      const storeId = String(draftRow.row?.store?.id || "");
      if (
        !storeId ||
        (open && modalStoreId === storeId) ||
        !needsDynamicBaseline(draftRow.row, draftRow.draft?.targetChoice) ||
        dynamicBaselineLoadingByStoreId[storeId] ||
        dynamicBaselineErrorByStoreId[storeId]
      ) {
        return;
      }

      loadDynamicBaselineForStore(storeId);
    });
  }, [
    draftRows,
    dynamicBaselineErrorByStoreId,
    dynamicBaselineLoadingByStoreId,
    loadDynamicBaselineForStore,
    modalStoreId,
    open,
  ]);

  React.useEffect(() => {
    if (
      !open ||
      !modalStoreId ||
      !activeStorePlan ||
      !needsDynamicBaseline(activeStorePlan, selectedTargetChoice) ||
      modalDynamicBaselineLoading ||
      modalDynamicBaselineError
    ) {
      return;
    }

    loadDynamicBaselineForStore(modalStoreId);
  }, [
    activeStorePlan,
    loadDynamicBaselineForStore,
    modalDynamicBaselineError,
    modalDynamicBaselineLoading,
    modalStoreId,
    open,
    selectedTargetChoice,
  ]);

  React.useEffect(() => {
    if (!open || !modalData?.id || !activeStorePlan?.planRow) {
      return;
    }
    setModalPlanRow(activeStorePlan.planRow);
  }, [activeStorePlan, modalData?.id, open]);

  React.useEffect(() => {
    if (!open || !modalData?.id || !activeStorePlan) {
      return;
    }

    const nextDraft = {
      targetChoice: selectedTargetChoice,
      targetValue,
      adjustmentValue,
    };
    const draftValues = getLaborPlanDraftValues(
      selectedTargetChoice,
      targetValue,
      adjustmentValue
    );
    const draftChange = getLaborPlanDraftChange(activeStorePlan, draftValues);
    const storeId = String(modalData.id);

    setDraftChangesByStoreId((currentDrafts) => {
      const currentDraft = currentDrafts[storeId];
      if (!draftChange.hasScenarioChange) {
        if (!currentDraft) {
          return currentDrafts;
        }
        const nextDrafts = { ...currentDrafts };
        delete nextDrafts[storeId];
        return nextDrafts;
      }

      if (draftInputsEqual(currentDraft, nextDraft)) {
        return currentDrafts;
      }

      return {
        ...currentDrafts,
        [storeId]: nextDraft,
      };
    });
  }, [
    activeStorePlan,
    adjustmentValue,
    modalData,
    open,
    selectedTargetChoice,
    targetValue,
  ]);

  function handleOpen(store) {
    const planRow = planRowByStoreId.get(store.id);
    const storeKey = String(store.id);
    const existingDraft = draftChangesByStoreId[storeKey];
    const draftInput = existingDraft || getSavedDraftInput(store, planRow);
    const safeDraftInput =
      draftInput.targetChoice === TARGET_LABOR_PERCENT &&
      !canUseFixedLaborPercentTarget(store, planRow)
        ? getSavedDraftInput(store, planRow)
        : draftInput;

    setDynamicBaselineErrorByStoreId((current) => {
      if (!current[storeKey]) {
        return current;
      }
      const next = { ...current };
      delete next[storeKey];
      return next;
    });
    setModalData(store);
    setModalPlanRow(planRow || null);
    setSelectedTargetChoice(safeDraftInput.targetChoice);
    setTargetValue(safeDraftInput.targetValue);
    setAdjustmentValue(safeDraftInput.adjustmentValue);
    setOpen(true);
  }

  function handleClose() {
    if (!publishing) {
      setOpen(false);
    }
  }

  function handleTargetChoice(nextChoice) {
    if (
      nextChoice === TARGET_LABOR_PERCENT &&
      !modalFixedLaborPercentAvailable
    ) {
      return;
    }

    const previousChoice = selectedTargetChoice;
    setSelectedTargetChoice(nextChoice);

    if (nextChoice === TARGET_DYNAMIC) {
      const savedAdjustment = getEffectiveDynamicAdjustmentPercent(
        modalData,
        modalPlanRow
      );
      setAdjustmentValue(
        savedAdjustment === null || savedAdjustment === undefined
          ? "0"
          : String(Number(savedAdjustment.toFixed(2)))
      );
      return;
    }

    if (
      nextChoice === TARGET_LABOR_PERCENT &&
      previousChoice !== TARGET_LABOR_PERCENT
    ) {
      const actualLaborPercent = modalPlanRow?.actual?.labor_percent;
      if (actualLaborPercent !== null && actualLaborPercent !== undefined) {
        setTargetValue(String(Number(actualLaborPercent).toFixed(1)));
      }
      return;
    }

    if (nextChoice === TARGET_SPLH && previousChoice !== TARGET_SPLH) {
      const actualSplh = modalPlanRow?.actual?.splh;
      if (actualSplh !== null && actualSplh !== undefined) {
        setTargetValue(String(Number(actualSplh).toFixed(1)));
      }
    }
  }

  function handleResetStoreDraft() {
    if (!modalData?.id) {
      return;
    }
    const savedDraft = getSavedDraftInput(modalData, modalPlanRow);
    const storeId = String(modalData.id);

    setDraftChangesByStoreId((currentDrafts) => {
      if (!currentDrafts[storeId]) {
        return currentDrafts;
      }
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[storeId];
      return nextDrafts;
    });
    setSelectedTargetChoice(savedDraft.targetChoice);
    setTargetValue(savedDraft.targetValue);
    setAdjustmentValue(savedDraft.adjustmentValue);
  }

  function handleDiscardDrafts() {
    setDraftChangesByStoreId({});
    setPublishConfirmOpen(false);
    if (modalData && modalPlanRow) {
      const savedDraft = getSavedDraftInput(modalData, modalPlanRow);
      setSelectedTargetChoice(savedDraft.targetChoice);
      setTargetValue(savedDraft.targetValue);
      setAdjustmentValue(savedDraft.adjustmentValue);
    }
  }

  function handleRequestPublishDrafts() {
    if (draftRows.length === 0) {
      toast.info("No queued labor plan changes");
      return;
    }
    if (invalidDraftCount > 0) {
      toast.error("Fix incomplete store targets before publishing");
      return;
    }
    setPublishConfirmOpen(true);
  }

  async function handlePublishDrafts() {
    if (draftRows.length === 0) {
      toast.info("No queued labor plan changes");
      return;
    }
    if (invalidDraftCount > 0) {
      toast.error("Fix incomplete store targets before publishing");
      return;
    }
    if (!props.post_labor_plan_changes) {
      toast.error("Labor plan publish is unavailable");
      return;
    }

    const changes = draftRows.map((draftRow) =>
      buildLaborPlanChangePayload(draftRow.row, draftRow.draft)
    );
    const publishedCount = draftRows.length;

    setPublishing(true);
    try {
      await props.post_labor_plan_changes?.(changes, {
        applyForecasted: true,
        forecastStartDate: laborPlanSummary.series_window?.forecast_start_date || null,
        forecastEndDate: laborPlanSummary.series_window?.forecast_end_date || null,
      });
      setDraftChangesByStoreId({});
      setPublishConfirmOpen(false);
      setPublishedState({ count: publishedCount });
      if (publishedStateTimerRef.current) {
        clearTimeout(publishedStateTimerRef.current);
      }
      publishedStateTimerRef.current = setTimeout(() => {
        setPublishedState(null);
        publishedStateTimerRef.current = null;
      }, 6000);
      setOpen(false);
    } finally {
      setPublishing(false);
    }
  }

  return (
    <>
      <div className="mt-6 px-6">
        <LaborPlanInfoPanel />

        <div className="mt-5 flex flex-wrap items-start justify-between gap-6">
          <div className="flex flex-wrap items-start gap-5 text-sm text-slate-500">
            <div>
              <p className="mb-2 h-4 text-xs font-bold uppercase leading-4 tracking-normal text-slate-500">
                Recent history
              </p>
              <TimeframeSelector
                selectedTimeframe={props.laborPlanTimeframe || "4W"}
                onTimeframeChange={props.onLaborPlanTimeframeChange}
              />
            </div>
            <div>
              <p className="mb-2 h-4 text-xs font-bold uppercase leading-4 tracking-normal text-slate-500">
                Stores included
              </p>
              <CompanyStoreSelector
                stores={storesInfo}
                selectedStoreIds={selectedCompanyStoreIds}
                onChange={setCompanyPlanStoreIds}
              />
            </div>
          </div>
          {!props.readOnly ? (
            <div className="w-[560px] max-w-full shrink-0">
              <LaborPlanDraftPublishBar
                draftCount={draftRows.length}
                invalidDraftCount={invalidDraftCount}
                scenarioSummary={draftScenarioSummary}
                draftSummaries={draftStoreSummaries}
                publishedState={publishedState}
                forecastWindowLabel={windowLabels?.forecast}
                onDiscard={handleDiscardDrafts}
                onPublish={handleRequestPublishDrafts}
                publishing={publishing}
              />
            </div>
          ) : null}
        </div>

        <div className="relative">
          {props.laborPlanSummaryLoading ? (
            <div
              className="pointer-events-none fixed bottom-0 right-0 top-0 z-50 flex items-center justify-center"
              style={LABOR_PLAN_REFRESH_OVERLAY_STYLE}
              aria-live="polite"
              aria-label="Refreshing labor plan"
            >
              <div className="rounded-full bg-white/90 p-4 shadow-sm ring-1 ring-violet-100">
                <LoadSpinner />
              </div>
            </div>
          ) : null}

          <div
            className={`transition-opacity duration-150 ${
              props.laborPlanSummaryLoading ? "opacity-60" : "opacity-100"
            }`}
          >
            <LaborPlanMetricSummary
              aggregate={aggregate}
              laborPercentEnabled={laborPercentEnabled}
              draftScenarios={companyDraftScenarios}
              windowLabels={windowLabels}
              windowDayCounts={windowDayCounts}
              storeCount={selectedStoreCount}
            />

            <LaborPlanComparisonChart
              seriesRows={companySeriesRows}
              scenarioSeriesRows={scenarioSeriesRows}
              storeCount={selectedStoreCount}
            />

            <div className="mt-8 border-t border-slate-200 pt-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">
                    Store plans
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Compare each store's recent performance, forecasted plan, and labor trend. Largest gaps are shown first.
                  </p>
                  <StorePlanTrendLegend showDraft={draftScenarios.length > 0} />
                </div>
                <div className="relative">
                  <SearchIcon
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    sx={{ fontSize: 20 }}
                  />
                  <input
                    className="h-11 w-[280px] rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none focus:border-violet-700 focus:ring-2 focus:ring-violet-100"
                    placeholder="Search stores..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {filteredStorePlans.map((row) => {
                  const draftRow = draftRowByStoreId.get(String(row.store?.id));
                  const storeSeriesRows =
                    storeSeriesByStoreId[String(row.store?.id)] || [];
                  const storeScenarioRows = draftRow?.scenario
                    ? getScenarioSeriesRows(
                        storeSeriesRows,
                        comparisonConfig,
                        draftRow.scenario
                      )
                    : null;

                  return (
                    <StoreLaborPlanCard
                      key={row.store?.id}
                      row={row}
                      seriesRows={storeSeriesRows}
                      scenario={draftRow?.scenario || null}
                      scenarioSeriesRows={storeScenarioRows}
                      draft={draftRow?.draft || null}
                      draftLoading={Boolean(draftRow?.baselineLoading)}
                      draftActive={Boolean(draftRow)}
                      draftValidationMessage={draftRow?.validationMessage || null}
                      windowLabels={windowLabels}
                      windowDayCounts={windowDayCounts}
                      onEdit={props.readOnly ? null : handleOpen}
                    />
                  );
                })}
                {filteredStorePlans.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
                    No stores match the current filters.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Drawer open={open} anchor="right" onClose={handleClose}>
        <div className="relative flex h-full w-[520px] flex-col">
          <div className="max-h-screen flex-1 overflow-auto pb-[104px]">
            <DrawerHeader title="Edit labor plan" onClose={handleClose} />
            <div className="w-full px-6">
              <p className="mt-1 text-sm font-semibold text-slate-700">
                {modalData?.store_name || "Store"}
              </p>
              <div
                className={`mt-5 rounded-lg border px-4 py-3 text-sm leading-5 ${modalDraftStatus.className}`}
              >
                <p className={`font-semibold ${modalDraftStatus.titleClassName}`}>
                  {modalDraftStatus.title}
                </p>
                <p className="mt-1">{modalDraftStatus.message}</p>
              </div>

              <div className="mt-7">
                <h3 className="text-base font-semibold text-slate-900">
                  Choose target method
                </h3>
                <div className="mt-4 space-y-3">
                  {modalTargetOptions.map((option) => (
                    <TargetChoiceCard
                      key={option.key}
                      option={option}
                      selected={selectedTargetChoice === option.key}
                      onClick={() => handleTargetChoice(option.key)}
                    />
                  ))}
                </div>
              </div>

              {selectedTargetChoice === TARGET_DYNAMIC ? (
                <DynamicAdjustmentSlider
                  value={adjustmentValue}
                  onChange={setAdjustmentValue}
                />
              ) : selectedTargetChoice === TARGET_LABOR_PERCENT ||
              selectedTargetChoice === TARGET_SPLH ? (
                <div className="mt-5">
                  <label className="mb-2 block text-sm font-semibold text-slate-600">
                    {selectedTargetChoice === TARGET_LABOR_PERCENT
                      ? "Fixed labor percent"
                      : "Fixed SPLH"}
                  </label>
                  <TextField
                    fullWidth
                    type="number"
                    value={targetValue}
                    onChange={(event) => setTargetValue(event.target.value)}
                    InputProps={{
                      startAdornment:
                        selectedTargetChoice === TARGET_SPLH ? (
                          <InputAdornment position="start">$</InputAdornment>
                        ) : null,
                      endAdornment:
                        selectedTargetChoice === TARGET_LABOR_PERCENT ? (
                          <InputAdornment position="end">%</InputAdornment>
                        ) : null,
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01,
                      "aria-label":
                        selectedTargetChoice === TARGET_LABOR_PERCENT
                          ? "Fixed labor percent"
                          : "Fixed SPLH",
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#ffffff",
                        color: "#0f172a",
                        fontSize: "18px",
                        "& fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&:hover fieldset": {
                          borderColor: "#a78bfa",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#6d28d9",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputAdornment-root .MuiTypography-root": {
                        color: "#64748b",
                        fontSize: "18px",
                        fontWeight: 600,
                      },
                      "& input": {
                        paddingBottom: "14px",
                        paddingTop: "14px",
                      },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                      "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                    }}
                  />
                </div>
              ) : null}

              <div className="mt-6 grid grid-cols-2 gap-3">
                <DrawerMetricPreviewCard
                  title="Recent actual"
                  metric={modalPlanRow?.actual}
                  laborPercentEnabled={modalLaborPercentEnabled}
                  metricWindowLabel={windowLabels?.historical}
                />
                <DrawerMetricPreviewCard
                  title="Draft forecast"
                  metric={modalTargetMetric}
                  laborPercentEnabled={modalLaborPercentEnabled}
                  metricWindowLabel={windowLabels?.forecast}
                  impactPercent={modalPreview.percentDelta}
                  tone="orange"
                  loading={modalDynamicBaselinePending}
                />
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 z-30 flex h-[88px] w-full items-center border-t border-slate-200 bg-white px-6">
            <div className="grid w-full grid-cols-2 gap-3">
              <button
                type="button"
                className={`w-full rounded-lg border px-4 py-2.5 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700 ${
                  modalDraftActive
                    ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                }`}
                onClick={handleResetStoreDraft}
                disabled={!modalDraftActive || publishing}
              >
                Reset store
              </button>
              <button
                type="button"
                className="w-full rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
                onClick={handleClose}
              >
                Done editing
              </button>
            </div>
          </div>
        </div>
      </Drawer>

      <LaborPlanPublishConfirmModal
        open={publishConfirmOpen}
        publishing={publishing}
        draftCount={draftRows.length}
        invalidDraftCount={invalidDraftCount}
        scenarioSummary={draftScenarioSummary}
        draftSummaries={draftStoreSummaries}
        forecastWindowLabel={windowLabels?.forecast}
        onClose={() => setPublishConfirmOpen(false)}
        onConfirm={handlePublishDrafts}
      />

    </>
  );
}

export default LocationDetailsLaborPlanTable;
